// ═══════════════════════════════════════════════════════════════
// v3.3.923 — إدارة الكول سنتر (سنترال Yeastar P-Series)
// تبويبات: لوحة (حالة الاتصال + اتصال سريع) / الشلوح / سجل المكالمات
// (مع مطابقة الأرقام بالعملاء والمسوقين والمحتملين + نقرة اتصال) /
// التسجيلات (تشغيل وتنزيل عبر بروكسي الخادم) / الإعدادات (أدمن).
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from "react";
import { yeastar, tgSms, waBiz, portalAdmin } from "../services/api";
import NotesBox from "../components/NotesBox"; // v3.4.806 — بيانات إضافية لإعدادات الكول سنتر
import WaEmbeddedSignup from "../components/WaEmbeddedSignup"; // v3.4.802 — ربط رقم واتساب للأعمال (Embedded Signup / Coexistence) // v3.3.934 tgSms / v3.3.939 waBiz / v3.4.179 portalAdmin (المستخدم المرتبط بالتحويلة)
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import WaMedia, { isPlaceholderBody } from "../components/WaMedia"; // v3.4.091 — مرفقات وارد واتس الأعمال
import { FiPhoneCall, FiRefreshCw, FiSettings, FiUsers, FiList, FiMic, FiActivity, FiDownload, FiPhone } from "react-icons/fi";

const fmtDur = (s) => { const n = Number(s) || 0; return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`; };
const DISPO = { ANSWERED: ["مجابة", "#dcfce7", "#166534"], "NO ANSWER": ["بلا رد", "#fef3c7", "#92400e"], BUSY: ["مشغول", "#fee2e2", "#991b1b"], FAILED: ["فاشلة", "#fee2e2", "#991b1b"], VOICEMAIL: ["بريد صوتي", "#e0e7ff", "#3730a3"] };
const CTYPE = { Inbound: ["واردة", "#dbeafe", "#1d4ed8"], Outbound: ["صادرة", "#dcfce7", "#166534"], Internal: ["داخلية", "#f1f5f9", "#475569"] };
const chip = ([l, bg, c]) => <span style={{ background: bg, color: c, borderRadius: 10, padding: "2px 10px", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>{l}</span>;
const KIND_COLOR = { "عميل": ["#dcfce7", "#166534"], "مسوق": ["#fef3c7", "#92400e"], "عميل محتمل": ["#e0e7ff", "#3730a3"] };

const inputSt = { padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none" };
const btnSt = (bg = "#0369a1") => ({ padding: "8px 16px", background: bg, color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 });
const th = { padding: "9px 12px", textAlign: "right", fontSize: 11.5, color: "#475569", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", background: "#f8fafc" };
const td = { padding: "8px 12px", borderTop: "1px solid #f1f5f9", fontSize: 12.5 };

// v3.3.928 — فرز وتصفية من رأس الجدول: ضغطة على العنوان تفرز، وحقل تصفية لكل عمود
const timeKey = (s) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i.exec(String(s || "").trim());
  if (!m) return String(s || "");
  let h = +m[4] % 12; if (/pm/i.test(m[7] || "")) h += 12; if (!m[7]) h = +m[4];
  return `${m[3]}${m[1].padStart(2, "0")}${m[2].padStart(2, "0")}${String(h).padStart(2, "0")}${m[5]}${m[6]}`;
};
function useTableCtl(cols, filterRows) {
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState(1);
  const [filters, setFilters] = useState({});
  // العمود ذو القيم القليلة يصير دروب مينيو → مطابقة تامة؛ الكثير يبقى كتابة → مطابقة جزئية
  const isSelect = (c) => new Set((filterRows || []).map((row) => String(c.get(row) ?? "").trim()).filter(Boolean)).size <= 40;
  const apply = (rows) => {
    let r = rows;
    for (const c of cols) {
      const raw = String(filters[c.key] || "").trim();
      if (!raw || c.filter === false) continue;
      if (isSelect(c)) r = r.filter((row) => String(c.get(row) ?? "").trim() === raw);
      else { const q = raw.toLowerCase(); r = r.filter((row) => String(c.get(row) ?? "").toLowerCase().includes(q)); }
    }
    const c = cols.find((x) => x.key === sortKey);
    if (c) {
      const g = c.sortGet || c.get;
      r = [...r].sort((a, b) => {
        const x = g(a), y = g(b);
        if (c.num) return ((parseFloat(x) || 0) - (parseFloat(y) || 0)) * dir;
        return String(x ?? "").localeCompare(String(y ?? ""), "ar", { numeric: true }) * dir;
      });
    }
    return r;
  };
  const header = (
    <thead>
      <tr>
        {cols.map((c) => c.label === "" ? <th key={c.key} style={th} /> : (
          <th key={c.key} style={{ ...th, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
            onClick={() => { if (sortKey === c.key) setDir(-dir); else { setSortKey(c.key); setDir(1); } }}
            title="اضغط للفرز">
            {c.label} {sortKey === c.key ? (dir === 1 ? "▲" : "▼") : <span style={{ color: "#cbd5e1" }}>⇅</span>}
          </th>
        ))}
      </tr>
      <tr>
        {cols.map((c) => {
          if (c.label === "" || c.filter === false) return <th key={c.key} style={{ ...th, padding: "4px 8px", background: "#fff" }} />;
          // v3.3.931 — التصفية دروب مينيو: القيم المميزة للعمود (وإن كثرت نبقي حقل كتابة)
          const distinct = [...new Set((filterRows || []).map((row) => String(c.get(row) ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar", { numeric: true }));
          const fSt = { width: "100%", minWidth: 56, boxSizing: "border-box", padding: "3px 6px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontFamily: "inherit", outline: "none", background: "#fff" };
          return (
            <th key={c.key} style={{ ...th, padding: "4px 8px", background: "#fff" }}>
              {distinct.length <= 40 ? (
                <select value={filters[c.key] || ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })} style={fSt}>
                  <option value="">الكل</option>
                  {distinct.map((v) => <option key={v} value={v}>{v.length > 40 ? v.slice(0, 40) + "…" : v}</option>)}
                </select>
              ) : (
                <input value={filters[c.key] || ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })} placeholder="تصفية…" style={fSt} />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
  return { apply, header };
}

// شلحي (المتصل) — يُحفظ محليًا في المتصفح لكل مستخدم
const getMyExt = () => localStorage.getItem("yeastar_my_ext") || "";
const setMyExtLS = (v) => localStorage.setItem("yeastar_my_ext", v);

function MyExtBox({ myExt, setMyExt }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "5px 10px" }}>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: "#0369a1" }}>تحويلتي:</span>
      <input value={myExt} onChange={(e) => { setMyExt(e.target.value); setMyExtLS(e.target.value); }} placeholder="مثال 1001"
        dir="ltr" style={{ ...inputSt, width: 80, padding: "4px 8px", fontSize: 12 }} />
    </div>
  );
}

function DialBtn({ number, myExt, small }) {
  const [busy, setBusy] = useState(false);
  if (!number) return null;
  const dial = async () => {
    if (!myExt) return toast.error("أدخل رقم تحويلتك الداخلية أولًا (أعلى الصفحة)");
    setBusy(true);
    try { await yeastar.dial({ caller: myExt, callee: number }); toast.success(`جارٍ الاتصال بـ ${number} — سترن تحويلتك ${myExt} أولًا`); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الاتصال"); }
    setBusy(false);
  };
  return (
    <button onClick={dial} disabled={busy} title={`اتصال بـ ${number}`}
      style={{ ...btnSt("#059669"), padding: small ? "3px 8px" : "8px 14px", fontSize: small ? 11 : 12.5 }}>
      <FiPhone size={small ? 11 : 14} />{small ? "" : "اتصال"}
    </button>
  );
}

function CallCenterPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating);
  const [tab, setTab] = useState("dash");
  const [myExt, setMyExt] = useState(getMyExt());
  const [status, setStatus] = useState(null); // {ok, extensions_total, extensions_online, error, not_configured}
  const loadStatus = useCallback(() => {
    yeastar.status().then((r) => setStatus(r.data)).catch((e) => setStatus({ ok: false, error: e.response?.data?.error || "تعذر الوصول للخادم", not_configured: e.response?.data?.not_configured }));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // v3.4.177 — «التحويلات الداخلية» و«توجيه الشرائح» انتقلا إلى تابات فرعية داخل «الإعدادات»؛ التاب يظهر للأدمن أو من لديه صلاحية callcenter_can_settings
  const canSettings = isAdmin || !!user?.callcenter_can_settings;
  const TABS = [
    ["dash", "📊 اللوحة", FiActivity], ["cdr", "📞 سجل المكالمات", FiList],
    ["rec", "🎙 التسجيلات", FiMic],
    ["sms", "📨 الرسائل SMS", FiList], // v3.3.934
    // v3.4.042 — تبويب واتساب بزنس انتقل لصفحة مستقلة «إدارة البزنس شات» في القائمة الجانبية
    ...(canSettings ? [["settings", "⚙️ الإعدادات", FiSettings]] : []),
  ];
  const [setSub, setSetSub] = useState("ext"); // v3.4.177 — التاب الفرعي داخل الإعدادات: ext | routing | pbx

  return (
    <div style={{ padding: 18, maxWidth: 1250, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}><FiPhoneCall /> إدارة الكول سنتر</h2>
        {status && (status.ok
          ? <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 12, padding: "3px 12px", fontSize: 11.5, fontWeight: 800 }}>● متصل بالسنترال — {status.extensions_online}/{status.extensions_total} تحويلة مسجلة</span>
          : <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 12, padding: "3px 12px", fontSize: 11.5, fontWeight: 800 }}>● غير متصل{status.error ? ` — ${status.error}` : ""}</span>)}
        <button onClick={loadStatus} style={{ ...btnSt("#64748b"), padding: "5px 10px", fontSize: 11.5 }}><FiRefreshCw size={12} /> تحديث</button>
        <div style={{ marginInlineStart: "auto" }}><MyExtBox myExt={myExt} setMyExt={setMyExt} /></div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: "7px 16px", borderRadius: 10, fontSize: 13, fontWeight: tab === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
              border: tab === k ? "2px solid #0369a1" : "1px solid #e2e8f0", background: tab === k ? "#f0f9ff" : "#fff", color: tab === k ? "#0369a1" : "#64748b" }}>{l}</button>
        ))}
      </div>

      {tab === "dash" && <DashTab status={status} myExt={myExt} isAdmin={canSettings} goSettings={() => { setSetSub("pbx"); setTab("settings"); }} />}
      {tab === "cdr" && <CdrTab myExt={myExt} />}
      {tab === "rec" && <RecordingsTab />}
      {tab === "sms" && <SmsTab isAdmin={canSettings} />}
      {/* v3.4.177 — الإعدادات: تابات فرعية منفصلة */}
      {tab === "settings" && canSettings && (
        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", paddingInlineStart: 8, borderInlineStart: "3px solid #bae6fd" }}>
            {[["ext", "☎️ التحويلات الداخلية"], ["routing", "🔀 توجيه الشرائح"], ["pbx", "🔌 ربط السنترال (Yeastar)"], ["tg", "📟 ربط الجايتواي (SMS)"], ["intake", "📥 إدخال المتصلين إلى CRM"]].map(([k, l]) => ( /* v3.4.397 */
              <button key={k} onClick={() => setSetSub(k)}
                style={{ padding: "6px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: setSub === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
                  border: setSub === k ? "2px solid #0369a1" : "1px solid #e2e8f0", background: setSub === k ? "#f0f9ff" : "#fff", color: setSub === k ? "#0369a1" : "#64748b" }}>{l}</button>
            ))}
          </div>
          {setSub === "ext" && <ExtensionsTab isAdmin={canSettings} />}
          {setSub === "routing" && <RoutingTab isAdmin={canSettings} />}
          {setSub === "pbx" && <SettingsTab onSaved={loadStatus} />}
          {setSub === "tg" && <div><div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}><NotesBox load={tgNotesLoad} save={tgNotesSave} title="ربط الجايتواي (SMS)" placeholder="اكتب هنا: بيانات دخول الجايتواي، المنافذ، الشرائح وأرقامها، ما تم وما بقي…" hint="يراه ويحرره من يملك صلاحية إعدادات الكول سنتر" /></div><TgGatewaySettingsForm /></div>}{/* v3.4.397 — v3.4.806 بيانات إضافية */}
          {setSub === "intake" && <CallIntakeTab />}{/* v3.4.808 */}
        </div>
      )}
    </div>
  );
}

// ── v3.4.180 — صفحة مستقلة تحت «إدارة المكتب»: واتس الرقم الرئيسي للشركة (ليس رقم تسويق) ──
export function CompanyWhatsAppPage({ variant = "company" }) {
  // v3.4.743 — شاشة واحدة للرقمين الرسميين (طلب المالك): بطاقتان منفصلتان — 🏢 الرقم الرئيسي للشركة و📍 رقم دليلك الرسمي — وأسفلهما محادثات الرقم المختار.
  // variant أو ?n= يحددان الرقم المفتوح أولًا (المسارات القديمة /company-whatsapp و /daleelak-whatsapp تبقى تعمل).
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating);
  const canCompany = isAdmin || !!user?.can_view_company_whatsapp, canDaleelak = isAdmin || !!user?.can_view_daleelak_whatsapp;
  const [which, setWhich] = useState(() => { let n = ""; try { n = new URLSearchParams(window.location.search).get("n") || ""; } catch (e) { /* ignore */ } n = n || variant; return n === "daleelak" && canDaleelak ? "daleelak" : canCompany ? "company" : "daleelak"; });
  const [cards, setCards] = useState({ company: null, daleelak: null });
  const load = useCallback(() => {
    if (canCompany) waBiz.numbers({ scope: "company" }).then((r) => { const l = r.data?.numbers || []; setCards((c) => ({ ...c, company: l.find((n) => n.is_company_main) || (r.data?.company_main ? { display_number: r.data.company_main } : null) })); }).catch(() => {});
    if (canDaleelak) waBiz.numbers({ scope: "daleelak" }).then((r) => { const l = r.data?.numbers || []; setCards((c) => ({ ...c, daleelak: l.find((n) => n.is_daleelak) || (r.data?.daleelak ? { display_number: r.data.daleelak } : null) })); }).catch(() => {});
  }, [canCompany, canDaleelak]);
  useEffect(() => { load(); const t = setInterval(load, 60000); const onFocus = () => load(); window.addEventListener("focus", onFocus); return () => { clearInterval(t); window.removeEventListener("focus", onFocus); }; }, [load]);
  const pick = (k) => { setWhich(k); try { window.history.replaceState(null, "", "?n=" + k); } catch (e) { /* ignore */ } };
  const fmtNum = (d) => { const s = String(d || ""); return /^966\d{9}$/.test(s) ? `+966 ${s.slice(3, 5)} ${s.slice(5, 8)} ${s.slice(8)}` : s; };
  const ago = (t) => { if (!t) return "—"; const d = new Date(t); if (isNaN(d)) return "—"; const now = new Date(); return d.toDateString() === now.toDateString() ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("en-CA"); };
  const rename = async (n) => {
    if (!n?.display_number) return;
    const name = window.prompt(`اسم الرقم ${n.display_number}:`, n.name || ""); if (name === null) return;
    try { await waBiz.renameNumber(n.display_number, name.trim()); toast.success("حُفظ الاسم ✓"); load(); } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };
  const card = (k, icon, title, n) => { const on = which === k; const unread = Number(n?.unread) || 0; return (
    <button key={k} type="button" onClick={() => pick(k)} title={n?.display_number ? fmtNum(n.display_number) : title}
      style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 16, cursor: "pointer", fontFamily: "inherit", textAlign: "start", minWidth: 0,
        border: on ? "2px solid #16a34a" : "1px solid #e2e8f0", background: on ? "linear-gradient(135deg,#f0fdf4,#ecfdf5)" : "#fff", boxShadow: on ? "0 8px 20px -12px rgba(22,163,74,.6)" : "0 1px 2px rgba(15,23,42,.04)", transition: "all .15s" }}>
      <span style={{ width: 44, height: 44, flex: "none", borderRadius: 14, display: "grid", placeItems: "center", fontSize: 20, background: on ? "#16a34a" : "#f1f5f9" }}>{icon}</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 800, color: on ? "#14532d" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n?.name || title}</span>
        <span dir="ltr" style={{ display: "block", fontSize: 12, color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{n?.display_number ? fmtNum(n.display_number) : "— لم يُربط رقم بعد —"}</span>
        <span style={{ display: "block", fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{n ? `💬 ${n.total || 0} · آخر نشاط ${ago(n.last_at)}` : title}</span>
      </span>
      {unread > 0 && <span style={{ position: "absolute", top: 8, insetInlineEnd: 10, background: "#dc2626", color: "#fff", borderRadius: 999, padding: "0 7px", fontSize: 11, fontWeight: 800, lineHeight: "18px", minWidth: 18, textAlign: "center" }}>{unread}</span>}
      <span style={{ position: "absolute", bottom: 8, insetInlineEnd: 10, fontSize: 9.5, fontWeight: 800, color: "#16a34a", background: "#dcfce7", borderRadius: 999, padding: "0 6px" }}>رسمي</span>
      {on && isAdmin && n?.display_number && <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); rename(n); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); rename(n); } }} title="تسمية الرقم" style={{ position: "absolute", bottom: 6, insetInlineStart: 10, fontSize: 11, color: "#7c3aed", cursor: "pointer" }}>✏️ تسمية</span>}
    </button>); };
  return (
    <div style={{ padding: 18, maxWidth: 1250, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>💬 واتساب الأرقام الرسمية</h2>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>الرقم الرئيسي للشركة ورقم «دليلك» الرسمي في شاشة واحدة — كل رقم ببطاقته المستقلة؛ اختر الرقم لعرض محادثاته أسفله.</div>
      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 16, padding: 10, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <b style={{ fontSize: 13, color: "#0f172a" }}>📱 الأرقام الرسمية</b>
          <span style={{ fontSize: 11.5, color: "#64748b" }}>{[cards.company, cards.daleelak].filter(Boolean).reduce((s, n) => s + (Number(n.unread) || 0), 0) || 0} غير مقروءة</span>
          <button onClick={load} style={{ ...btnSt("#64748b"), padding: "5px 10px", fontSize: 11.5, marginInlineStart: "auto" }}><FiRefreshCw size={12} /> تحديث</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 10 }}>
          {canCompany && card("company", "🏢", "الرقم الرئيسي للشركة", cards.company)}
          {canDaleelak && card("daleelak", "📍", "رقم دليلك الرسمي", cards.daleelak)}
        </div>
      </div>
      <WaBizTab key={which} isAdmin={isAdmin} scope={which} hideNumbers />
    </div>
  );
}

// ── v3.4.042 — صفحة مستقلة: إدارة البزنس شات (كانت تبويب «واتساب بزنس» داخل الكول سنتر) ──
export function BusinessChatPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating);
  return (
    <div style={{ padding: 18, maxWidth: 1250, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 14px", fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>💬 إدارة البزنس شات</h2>
      <WaBizTab isAdmin={isAdmin} />
    </div>
  );
}

// ── اللوحة: حالة + اتصال سريع ──
function DashTab({ status, myExt, isAdmin, goSettings }) {
  const [num, setNum] = useState("");
  const card = (title, value, color) => (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, minWidth: 180, flex: 1 }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    </div>
  );
  return (
    <div>
      {status && !status.ok && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 13, color: "#991b1b" }}>
          <b>السنترال غير متصل.</b> {status.not_configured
            ? <>لم تُضبط الإعدادات بعد{isAdmin ? <> — <button onClick={goSettings} style={{ ...btnSt("#991b1b"), padding: "4px 10px", fontSize: 11.5 }}>فتح الإعدادات</button></> : " (يضبطها الأدمن من تبويب الإعدادات)"}.</>
            : <> {status.error}<div style={{ marginTop: 6, color: "#7f1d1d", fontSize: 12 }}>تنبيه: خادم النظام سحابي — رابط السنترال يجب أن يكون قابلاً للوصول من الإنترنت (Port Forwarding لمنفذ 8088 أو ميزة Remote Access/FQDN في Yeastar)، وليس عنوان الشبكة الداخلية فقط.</div></>}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        {card("حالة الاتصال بالسنترال", status?.ok ? "متصل ✓" : "غير متصل ✗", status?.ok ? "#166534" : "#991b1b")}
        {card("التحويلات الداخلية المسجلة", status?.ok ? `${status.extensions_online} / ${status.extensions_total}` : "—", "#0369a1")}
      </div>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, maxWidth: 520 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>📞 اتصال سريع</div>
        <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 10 }}>سترن تحويلتك ({myExt || "لم تُحدد"}) أولًا، وعند الرد يتصل السنترال بالرقم المطلوب.</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" style={{ ...inputSt, flex: 1 }} />
          <DialBtn number={num.trim()} myExt={myExt} />
        </div>
      </div>
      <ExtSimSummary />{/* v3.4.179 */}
    </div>
  );
}

// ── v3.4.179 — اللوحة: جدول مختصر واحد — الاسم | التحويلة | المستخدم المرتبط (حساب التطبيق) | الشرائح المخصصة له (المسارات الواردة الموجّهة إليه مباشرة أو عبر مجموعة رنين) ──
function ExtSimSummary() {
  const [exts, setExts] = useState(null);
  const [routing, setRouting] = useState(null);
  const [labels, setLabels] = useState({});
  const [rgs, setRgs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showAll, setShowAll] = useState(true); // v3.4.179 — كل التحويلات افتراضيًا (الفاعلة أولًا)
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    yeastar.extensions().then((r) => setExts(r.data?.extensions || [])).catch(() => setExts([]));
    yeastar.routing().then((r) => setRouting(r.data || { inbound: [] })).catch(() => setRouting({ inbound: [] }));
    yeastar.labels().then((r) => setLabels(r.data?.labels || {})).catch(() => {});
    yeastar.ringGroups().then((r) => setRgs(r.data?.groups || [])).catch(() => {});
    portalAdmin.directory().then((r) => setAccounts(r.data?.accounts || [])).catch(() => setAccounts([])); // v3.4.201 — يعمل لغير الأدمن
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!exts || !routing) return <div style={{ padding: 14, color: "#94a3b8", fontSize: 12 }}>جاري تحميل ملخص التحويلات والشرائح…</div>;
  // رقم الشريحة: الخانة المحفوظة لمسار الوارد، وإلا الرقم الظاهر في اسم المسار (INSIM1-0553665601-…)
  const simOf = (r) => (labels[`inbound:${r.id}`] || "").trim() || ((String(r.name || "").match(/0\d{9}|9665\d{8}/) || [])[0] || "");
  const simLabel = (r) => { const s = simOf(r); const n = String(r.name || "").replace(/^INSIM\d+-?/i, "").replace(/0\d{9}-?|9665\d{8}-?/, "").replace(/^-+|-+$/g, ""); return { sim: s, tag: n, route: r.name }; };
  const extNumOf = (v) => { const x = exts.find((e) => String(e.id) === String(v) || String(e.number) === String(v)); return x ? String(x.number) : String(v); };
  // التحويلة ← شرائحها
  const simsByExt = {};
  const push = (num, item) => { (simsByExt[num] = simsByExt[num] || []).push(item); };
  for (const r of routing.inbound || []) {
    const it = simLabel(r);
    if (r.def_dest === "extension") push(extNumOf(r.def_dest_value), { ...it, via: "" });
    else if (r.def_dest === "ring_group") {
      const g = rgs.find((g) => String(g.id) === String(r.def_dest_value) || String(g.number) === String(r.def_dest_value));
      if (g) for (const m of g.members || []) push(extNumOf(m.value), { ...it, via: g.name });
    }
  }
  const accountOf = (num) => accounts.find((a) => String(a.yeastar_extension || "").trim() === String(num));
  const rows = exts.map((x) => ({ x, sims: simsByExt[String(x.number)] || [], acc: accountOf(x.number) }))
    .sort((a, b) => ((b.sims.length > 0) - (a.sims.length > 0)) || ((b.acc ? 1 : 0) - (a.acc ? 1 : 0)) || ((b.x.online ? 1 : 0) - (a.x.online ? 1 : 0)) || String(a.x.number).localeCompare(String(b.x.number), undefined, { numeric: true })); // v3.4.179 — الفاعلة أولًا
  const active = rows.filter((r) => r.sims.length || r.acc || r.x.online);
  const shown = (showAll ? rows : active).filter((r) => !q.trim() || [r.x.number, r.x.name, r.acc?.display_name, ...r.sims.map((s) => s.sim + s.tag)].join(" ").toLowerCase().includes(q.trim().toLowerCase()));
  const th = { padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#475569", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", background: "#f8fafc" };
  const td = { padding: "7px 10px", fontSize: 12.5, borderTop: "1px solid #f1f5f9", verticalAlign: "top" };
  const simsCount = Object.values(simsByExt).reduce((s, a) => s + a.length, 0);
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a" }}>📇 التحويلات والشرائح الفاعلة</div>
        <span style={{ fontSize: 11, color: "#64748b" }}>{active.length} تحويلة فاعلة · {simsCount} شريحة موجّهة</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم / التحويلة / الشريحة" style={{ ...inputSt, width: 220, padding: "5px 10px", fontSize: 12, marginInlineStart: "auto" }} />
        <label style={{ fontSize: 11.5, color: "#475569", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> إظهار كل التحويلات ({rows.length})
        </label>
        <button onClick={load} style={{ ...btnSt("#64748b"), padding: "5px 10px", fontSize: 11.5 }}><FiRefreshCw size={12} /> تحديث</button>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["الاسم", "التحويلة", "المستخدم المرتبط (التطبيق)", "الشرائح المخصصة", "التسجيل"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map(({ x, sims, acc }) => (
              <tr key={x.id}>
                <td style={{ ...td, fontWeight: 800, whiteSpace: "nowrap" }}>{x.name && x.name !== x.number ? x.name : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                <td style={{ ...td, fontFamily: "monospace", fontWeight: 900, direction: "ltr", textAlign: "right" }}>{x.number}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{acc ? <span style={{ background: "#f0fdfa", color: "#0f766e", borderRadius: 10, padding: "2px 9px", fontSize: 11.5, fontWeight: 800 }}>👤 {acc.display_name || acc.phone}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                <td style={td}>
                  {sims.length === 0 ? <span style={{ color: "#cbd5e1" }}>—</span> : (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {sims.map((s, i) => (
                        <span key={i} title={s.route + (s.via ? ` — عبر مجموعة الرنين ${s.via}` : "")} style={{ background: s.via ? "#fef3c7" : "#dcfce7", color: s.via ? "#92400e" : "#166534", borderRadius: 10, padding: "2px 9px", fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap" }}>
                          📶 <span dir="ltr">{s.sim || s.route}</span>{s.tag ? ` · ${s.tag}` : ""}{s.via ? " 🔔" : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{x.online ? <span style={{ color: "#166534", fontWeight: 800 }}>● مسجل</span> : <span style={{ color: "#94a3b8" }}>○ غير مسجل</span>}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} style={{ padding: 22, textAlign: "center", color: "#94a3b8" }}>لا توجد تحويلات فاعلة (مسجلة أو مرتبطة بمستخدم أو بشريحة) — فعّل «إظهار كل التحويلات»</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 6 }}>🔔 = الشريحة موجّهة إلى مجموعة رنين تضم هذه التحويلة · رقم الشريحة من خانة «رقم الشريحة/الجوال» في توجيه الشرائح أو من اسم المسار · المستخدم المرتبط من «حسابات التطبيق» (عمود التحويلة)</div>
    </div>
  );
}


// ── الشلوح ──
function ExtensionsTab({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [editId, setEditId] = useState(null); // v3.3.929 — تحويلة قيد التعديل
  const [sending, setSending] = useState(null);
  const load = useCallback(() => {
    yeastar.extensions().then((r) => setRows(r.data?.extensions || [])).catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setRows([]); });
  }, []);
  const sendWelcome = async (x) => {
    if (!x.email) return toast.error(`التحويلة ${x.number} بلا إيميل — أضفه من زر التعديل أولًا`);
    if (!window.confirm(`إرسال بيانات دخول تطبيق Linkus إلى ${x.email}؟`)) return;
    setSending(x.id);
    try { await yeastar.welcomeEmail(x.id, { number: x.number }); toast.success(`أُرسل الإيميل الترحيبي إلى ${x.email} ✓`); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الإرسال"); }
    setSending(null);
  };
  useEffect(() => { load(); }, [load]);
  const PRESENCE = { available: ["متاح", "#dcfce7", "#166534"], away: ["بعيد", "#fef3c7", "#92400e"], do_not_disturb: ["عدم إزعاج", "#fee2e2", "#991b1b"], business_trip: ["رحلة عمل", "#e0e7ff", "#3730a3"], lunch: ["استراحة", "#fef3c7", "#92400e"], off_work: ["خارج الدوام", "#f1f5f9", "#64748b"] };
  const ctl = useTableCtl([
    { key: "num", label: "التحويلة", get: (x) => x.number },
    { key: "name", label: "الاسم", get: (x) => x.name || "" },
    { key: "mob", label: "الجوال", get: (x) => x.mobile || "" },
    { key: "pres", label: "الحالة", get: (x) => (PRESENCE[x.presence] || [x.presence || ""])[0] },
    { key: "reg", label: "التسجيل", get: (x) => (x.online ? "مسجل" : "غير مسجل") },
    { key: "dev", label: "الأجهزة المتصلة", get: (x) => x.devices.join("، ") },
    { key: "act", label: "", get: () => "" },
  ], rows || []);
  return (
    <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        {ctl.header}
        <tbody>
          {ctl.apply(rows || []).map((x) => (
            <tr key={x.id}>
              <td style={{ ...td, fontWeight: 800, direction: "ltr", textAlign: "right" }}>{x.number}</td>
              <td style={td}>{x.name || "—"}</td>
              <td style={{ ...td, direction: "ltr", textAlign: "right" }}>{x.mobile || "—"}</td>
              <td style={td}>{chip(PRESENCE[x.presence] || [x.presence || "—", "#f1f5f9", "#64748b"])}</td>
              <td style={td}>{x.online ? chip(["مسجل ●", "#dcfce7", "#166534"]) : chip(["غير مسجل", "#fee2e2", "#991b1b"])}</td>
              <td style={{ ...td, fontSize: 11.5, color: "#64748b" }}>{x.devices.join("، ") || "—"}</td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                {isAdmin && <>
                  <button onClick={() => setEditId(x.id)} style={{ ...btnSt("#0369a1"), padding: "3px 10px", fontSize: 11, marginInlineEnd: 5 }}>✏️ تعديل</button>
                  <button onClick={() => sendWelcome(x)} disabled={sending === x.id}
                    style={{ ...btnSt("#7c3aed"), padding: "3px 10px", fontSize: 11 }}>{sending === x.id ? "…" : "📧 بيانات التطبيق"}</button>
                </>}
              </td>
            </tr>
          ))}
          {rows && rows.length === 0 && <tr><td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>لا توجد بيانات — تأكد من الاتصال بالسنترال</td></tr>}
          {!rows && <tr><td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</td></tr>}
        </tbody>
      </table>
      {editId && <ExtEditModal id={editId} onClose={() => setEditId(null)} onSaved={() => { setEditId(null); load(); }} />}
    </div>
  );
}

// ── v3.3.929 — نموذج تعديل تحويلة: الاسم/الإيميل/الجوال + عملاء Linkus ──
function ExtEditModal({ id, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    yeastar.extDetail(id).then((r) => setF(r.data)).catch((e) => { toast.error(e.response?.data?.error || "فشل جلب التفاصيل"); onClose(); });
  }, [id, onClose]);
  const save = async () => {
    setBusy(true);
    try {
      const { number, ...body } = f;
      await yeastar.updateExt(id, { ...body, number });
      toast.success("حُفظت التحويلة ✓"); onSaved();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  const inp = (label, key, props = {}) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, color: "#334155", marginBottom: 3 }}>{label}</div>
      <input value={f[key] || ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} style={{ ...inputSt, width: "100%", boxSizing: "border-box" }} {...props} />
    </div>
  );
  const tgl = (label, key) => (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155", cursor: "pointer" }}>
      <input type="checkbox" checked={!!f[key]} onChange={(e) => setF({ ...f, [key]: e.target.checked ? 1 : 0 })} /> {label}
    </label>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, width: 440, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        {!f ? <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div> : <>
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 12 }}>✏️ تعديل التحويلة {f.number}</div>
          {inp("الاسم الظاهر (Caller ID)", "caller_id", { placeholder: "مثال: أحمد - المبيعات" })}
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>{inp("الاسم الأول", "first_name")}</div>
            <div style={{ flex: 1 }}>{inp("اسم العائلة", "last_name")}</div>
          </div>
          {inp("الإيميل (لدخول تطبيق Linkus)", "email_addr", { dir: "ltr", placeholder: "user@company.com" })}
          {inp("رقم الجوال", "mobile_number", { dir: "ltr", placeholder: "05xxxxxxxx" })}
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#334155", marginBottom: 5 }}>عملاء Linkus المفعّلون:</div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
            {tgl("الجوال", "enb_mobile_client")}{tgl("سطح المكتب", "enb_desktop_client")}{tgl("الويب", "enb_web_client")}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ ...btnSt("#64748b") }}>إلغاء</button>
            <button onClick={save} disabled={busy} style={btnSt("#0f766e")}>{busy ? "جارٍ الحفظ…" : "💾 حفظ"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── v3.3.929 — توجيه الشرائح: الخطوط + المسارات الواردة (شريحة → صاحبها) والصادرة (المسموح لهم) ──
function RoutingTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [exts, setExts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [labels, setLabels] = useState({}); // v3.3.930 — {"inbound:5": "05xxxxxxxx"}
  const [rgs, setRgs] = useState([]); // v3.3.931 — مجموعات الرنين
  const [rgErr, setRgErr] = useState(null); // v3.3.932 — نظهر خطأ السنترال بدل الصمت
  const [rgLocal, setRgLocal] = useState(false);
  const [rgInfer, setRgInfer] = useState(null);
  const [rgAdmin, setRgAdmin] = useState(""); // v3.4.807 — رابط واجهة السنترال (لزر تعديل مجموعات الرنين) // v3.4.804 — نتيجة الاستنتاج من سجل المكالمات
  const [rgDays, setRgDays] = useState(60); // v3.4.180 — السجل المحلي (السنترال بلا واجهة لمجموعات الرنين)
  const load = useCallback(() => {
    yeastar.routing().then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setData({ trunks: [], inbound: [], outbound: [] }); });
    yeastar.extensions().then((r) => setExts(r.data?.extensions || [])).catch(() => {});
    yeastar.labels().then((r) => setLabels(r.data?.labels || {})).catch(() => {});
    yeastar.settings().then((r) => setRgAdmin(r.data?.admin_url || "")).catch(() => {}); // v3.4.807
    yeastar.ringGroups().then((r) => { setRgs(r.data?.groups || []); setRgLocal(!!r.data?.local); setRgErr(null); }).catch((e) => setRgErr(e.response?.data?.error || "تعذر جلب مجموعات الرنين"));
  }, []);
  useEffect(() => { load(); }, [load]);
  // v3.3.930 — خانة رقم الجوال/التلفون لكل عنصر (تُحفظ عند مغادرة الحقل)
  const PhoneCell = ({ k }) => {
    const [v, setV] = useState(labels[k] || "");
    useEffect(() => { setV(labels[k] || ""); }, [k]);
    const save = async () => {
      if ((labels[k] || "") === v.trim()) return;
      try { await yeastar.saveLabel(k, v.trim()); setLabels({ ...labels, [k]: v.trim() }); toast.success("حُفظ الرقم ✓"); }
      catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    };
    if (!isAdmin) return <span style={{ direction: "ltr", display: "inline-block", fontSize: 12 }}>{labels[k] || "—"}</span>;
    return (
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        placeholder="05xxxxxxxx" dir="ltr"
        style={{ ...inputSt, padding: "4px 8px", fontSize: 12, width: 120 }} />
    );
  };
  const box = (title, children) => (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
  if (!data) return <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div>;
  // v3.3.931 — الوجهة إما تحويلة "ext:<id>" أو مجموعة رنين "rg:<id>" (قيم السنترال بالمعرف لا بالرقم)
  const saveInbound = async (r, val) => {
    const [kind, id] = String(val).split(":");
    setBusy(`in${r.id}`);
    try {
      await yeastar.updateInbound(r.id, { def_dest: kind === "rg" ? "ring_group" : "extension", def_dest_value: String(id), name: r.name });
      toast.success(`حُدث توجيه «${r.name}» ✓`); load();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(null);
  };
  const destLabel = (r) => {
    if (r.def_dest === "extension") { const x = exts.find((e) => String(e.id) === String(r.def_dest_value) || String(e.number) === String(r.def_dest_value)); return x ? `تحويلة ${x.number}${x.name && x.name !== x.number ? ` — ${x.name}` : ""}` : `تحويلة ${r.def_dest_value}`; }
    if (r.def_dest === "ring_group") { const g = rgs.find((g) => String(g.id) === String(r.def_dest_value) || String(g.number) === String(r.def_dest_value)); return g ? `🔔 ${g.name}` : `مجموعة رنين ${r.def_dest_value}`; }
    return `${r.def_dest || "—"} ${r.def_dest_value || ""}`;
  };
  const inboundVal = (r) => {
    if (r.def_dest === "extension") { const x = exts.find((e) => String(e.id) === String(r.def_dest_value) || String(e.number) === String(r.def_dest_value)); return x ? `ext:${x.id}` : ""; }
    if (r.def_dest === "ring_group") { const g = rgs.find((g) => String(g.id) === String(r.def_dest_value) || String(g.number) === String(r.def_dest_value)); return g ? `rg:${g.id}` : ""; }
    return "";
  };
  // v3.4.180 — اسم/رقم المجموعة قابلان للتعديل في الوضع المحلي (يُحفظان عند مغادرة الحقل)
  const RgField = ({ g, field, placeholder, width }) => {
    const [v, setV] = useState(g[field] || "");
    useEffect(() => { setV(g[field] || ""); }, [g, field]);
    const save = async () => {
      if ((g[field] || "") === v.trim()) return;
      try { await yeastar.updateRingGroup(g.id, { member_list: g.members, name: field === "name" ? v.trim() : g.name, number: field === "number" ? v.trim() : g.number }); toast.success("حُفظ ✓"); load(); }
      catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    };
    return <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} placeholder={placeholder} dir={field === "number" ? "ltr" : "rtl"}
      style={{ ...inputSt, padding: "3px 8px", fontSize: 12, width, fontWeight: field === "name" ? 800 : 500 }} />;
  };
  const toggleRgMember = async (g, x, on) => {
    if (g.custom) return;
    const others = g.members.filter((m) => m.type !== "extension");
    const cur = g.members.filter((m) => m.type === "extension");
    const next = [...others, ...(on
      ? [...cur, { value: String(x.id), type: "extension" }]
      : cur.filter((m) => String(m.value) !== String(x.id) && String(m.text2) !== String(x.number)))];
    setBusy(`rg${g.id}`);
    try { await yeastar.updateRingGroup(g.id, { member_list: next, name: g.name }); toast.success(`حُدثت «${g.name}» ✓`); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(null);
  };
  // v3.4.804 — استنتاج أعضاء مجموعات الرنين من سجل المكالمات ثم تطبيقها على السجل المحلي
  const inferRg = async () => {
    setBusy("rginfer");
    try { const r = await yeastar.inferRingGroups(rgDays); setRgInfer(r.data); if (!(r.data?.groups || []).length) toast("لا توجد مكالمات إلى مجموعات رنين خلال هذه المدة", { icon: "ℹ️" }); }
    catch (e) { toast.error(e.response?.data?.error || "تعذّر قراءة سجل المكالمات"); }
    setBusy(null);
  };
  const applyRg = async (numbers) => {
    setBusy("rgapply");
    try { const r = await yeastar.syncRingGroups({ days: rgDays, numbers }); toast.success(`حُدثت ${(r.data?.applied || []).length} مجموعة من سجل المكالمات ✓`); setRgInfer(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل التحديث"); }
    setBusy(null);
  };
  const fmtTs = (t) => (t ? new Date(t * 1000).toLocaleDateString("en-CA") : "—");
  const toggleOutExt = async (r, x, on) => {
    // نحافظ على المجموعات/الأقسام المعرفة في السنترال ونعدل التحويلات فقط
    const groups = r.ext_list.filter((e) => e.type !== "extension");
    const cur = r.ext_list.filter((e) => e.type === "extension");
    const next = [...groups, ...(on
      ? [...cur, { id: x.id, ext_number: x.number, ext_name: x.name, type: "extension" }]
      : cur.filter((e) => String(e.id) !== String(x.id) && String(e.ext_number) !== String(x.number)))];
    setBusy(`out${r.id}`);
    try { await yeastar.updateOutbound(r.id, { ext_list: next, name: r.name }); toast.success(`حُدث «${r.name}» ✓`); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(null);
  };
  return (
    <div>
      {!isAdmin && <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 10, padding: 10, marginBottom: 12, fontSize: 12, color: "#92400e" }}>استعراض فقط — التعديل يتطلب صلاحية الأدمن.</div>}
      {box(`📡 الخطوط / الشرائح (${data.trunks.length})`, (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr>{["الاسم", "رقم الجوال/التلفون", "النوع", "الحالة"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.trunks.map((t) => (
                <tr key={t.id}>
                  <td style={{ ...td, fontWeight: 700 }}>{t.name}</td>
                  <td style={td}><PhoneCell k={`trunk:${t.id}`} /></td>
                  <td style={{ ...td, color: "#64748b" }}>{t.type}</td>
                  <td style={td}>{t.status === 0 ? chip(["متصل", "#dcfce7", "#166534"]) : chip([`حالة ${t.status}`, "#f1f5f9", "#64748b"])}</td>
                </tr>
              ))}
              {!data.trunks.length && <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>لا توجد خطوط</td></tr>}
            </tbody>
          </table>
        </div>
      ))}
      {box("📥 المسارات الواردة — كل شريحة ترن عند صاحبها", (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr>{["المسار", "رقم الشريحة/الجوال", "الخطوط/الشرائح", "الوجهة الحالية", "تحويل إلى تحويلة"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.inbound.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 700 }}>{r.name}</td>
                  <td style={td}><PhoneCell k={`inbound:${r.id}`} /></td>
                  <td style={{ ...td, fontSize: 11.5, color: "#64748b" }}>{r.trunks.join("، ") || "—"}</td>
                  <td style={{ ...td, fontSize: 11.5 }}>{destLabel(r)}</td>
                  <td style={td}>
                    {isAdmin ? (
                      <select disabled={busy === `in${r.id}`} value={inboundVal(r)}
                        onChange={(e) => e.target.value && saveInbound(r, e.target.value)} style={{ ...inputSt, padding: "5px 8px", fontSize: 12 }}>
                        <option value="">— اختر الوجهة —</option>
                        <optgroup label="تحويلات">
                          {exts.map((x) => <option key={x.id} value={`ext:${x.id}`}>{x.number}{x.name && x.name !== x.number ? ` — ${x.name}` : ""}</option>)}
                        </optgroup>
                        <optgroup label="مجموعات رنين">
                          {rgs.map((g) => <option key={g.id} value={`rg:${g.id}`}>🔔 {g.name} ({g.number})</option>)}
                        </optgroup>
                      </select>
                    ) : "—"}
                  </td>
                </tr>
              ))}
              {!data.inbound.length && <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>لا توجد مسارات واردة</td></tr>}
            </tbody>
          </table>
        </div>
      ))}
      {box(`🔔 مجموعات الرنين — من تشمل كل مجموعة (${rgs.length})`, (<>
        {rgLocal && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "8px 12px", marginBottom: 10, fontSize: 11.5, color: "#1e40af", lineHeight: 1.7 }}>
            ℹ️ السنترال لا يوفر واجهة API لمجموعات الرنين (INTERFACE NOT EXISTED) — لذلك تُسجَّل هنا محليًا: المجموعات المشار إليها في المسارات الواردة تظهر تلقائيًا، وأكمل اسمها ورقمها وأعضاءها كما هي في واجهة السنترال. يُستخدم هذا السجل في التوجيه واللوحة؛ والتعديل الفعلي للأعضاء يبقى من واجهة السنترال.
          </div>
        )}
        {rgLocal && isAdmin && (/* v3.4.804 */
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={inferRg} disabled={!!busy} style={btnSt("#0f766e")}>{busy === "rginfer" ? "⏳ جارٍ قراءة سجل المكالمات…" : "🔄 تحديث من سجل المكالمات (كما هو مطبّق فعليًا)"}</button>
              <select value={rgDays} onChange={(e) => setRgDays(Number(e.target.value))} style={{ ...inputSt, padding: "6px 8px", fontSize: 12 }}>
                {[30, 60, 90, 180].map((d) => <option key={d} value={d}>آخر {d} يومًا</option>)}
              </select>
              {(() => { /* v3.4.807 — فتح صفحة مجموعات الرنين في واجهة السنترال (التعديل الفعلي هناك) */
                let href = rgAdmin; try { if (rgAdmin) href = new URL(rgAdmin).origin + "/call_features/ring_group"; } catch { /* نبقي الرابط كما هو */ }
                return href
                  ? <a href={href} target="_blank" rel="noreferrer" title="يفتح واجهة السنترال (من شبكة المكتب) — Call Features → Ring Group" style={{ ...btnSt("#0369a1"), textDecoration: "none" }}>🛠 تعديل مجموعات الرنين (واجهة السنترال)</a>
                  : <span title="أدخل رابط واجهة السنترال في تبويب «ربط السنترال»" style={{ ...btnSt("#94a3b8"), cursor: "default" }}>🛠 تعديل مجموعات الرنين — أدخل رابط الواجهة أولًا</span>;
              })()}
              <span style={{ fontSize: 11, color: "#64748b" }}>يقرأ المكالمات التي وصلت لكل مجموعة فيستنتج اسمها ورقمها ومعرّفها (عبر المسار الوارد) ومن أجاب عليها فعلًا. السنترال لا يسجّل من رنّ دون إجابة، فالعضو الذي لم يجب خلال المدة لا يظهر — التعديل الفعلي للأعضاء من زر «تعديل مجموعات الرنين» (واجهة السنترال).</span>
            </div>
            {rgInfer && (
              <div style={{ marginTop: 8, border: "1px solid #99f6e4", background: "#f0fdfa", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                  <b style={{ fontSize: 12.5 }}>نتيجة الاستنتاج — {rgInfer.rows} مكالمة خلال {rgInfer.days} يومًا، {rgInfer.groups.length} مجموعة</b>
                  {rgInfer.groups.some((g) => g.members.length) && <button type="button" onClick={() => applyRg(null)} disabled={!!busy} style={{ ...btnSt("#166534"), padding: "5px 12px", fontSize: 12 }}>{busy === "rgapply" ? "⏳" : "✅ تطبيق الكل"}</button>}
                  <button type="button" onClick={() => setRgInfer(null)} style={{ ...btnSt("#fff"), color: "#64748b", border: "1px solid #e2e8f0", padding: "5px 10px", fontSize: 12 }}>إغلاق</button>
                </div>
                {rgInfer.groups.map((g) => (
                  <div key={g.number} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
                      <b>🔔 {g.name}</b>
                      <span style={{ color: "#64748b" }}>الرقم {g.number}</span>
                      <span style={{ color: "#94a3b8", fontSize: 10.5 }}>معرّف {g.id} ({{ local_number: "مطابق للسجل", inbound_route: "من المسار الوارد", number: "غير معروف — يُستخدم الرقم" }[g.id_source]})</span>
                      <span style={{ color: "#64748b" }}>{g.calls} مكالمة ({g.answered} مُجابة) · آخرها {fmtTs(g.last)}</span>
                      {g.local && <span style={{ color: "#92400e", fontSize: 11 }}>حاليًا: {g.local.name} / {g.local.number || "بلا رقم"} / {(g.local.members || []).length} عضو</span>}
                      {g.members.length > 0 && <button type="button" onClick={() => applyRg([g.number])} disabled={!!busy} style={{ ...btnSt("#0f766e"), padding: "3px 10px", fontSize: 11.5 }}>تطبيق</button>}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {g.members.length ? g.members.map((m) => (
                        <span key={m.number} title={`رنّت ${m.rang} مرة · أجابت ${m.answered} · آخرها ${fmtTs(m.last)}`} style={{ fontSize: 11.5, background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "2px 8px", color: "#0f766e" }}>
                          {m.number}{m.name && m.name !== m.number ? ` ${m.name}` : ""} <span style={{ color: "#94a3b8" }}>×{m.rang}</span>
                        </span>
                      )) : <span style={{ fontSize: 11.5, color: "#94a3b8" }}>لم تُرصد تحويلات رنّت مع هذه المجموعة</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {rgs.length ? rgs.map((g) => (
          <div key={g.id} style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 10, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
              {rgLocal && isAdmin ? <>🔔 <RgField g={g} field="name" placeholder={`مجموعة رنين ${g.id}`} width={180} /></> : <span style={{ fontWeight: 800, fontSize: 13 }}>🔔 {g.name}</span>}{/* v3.4.180 */}
              {rgLocal && isAdmin ? <span style={{ fontSize: 11, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>الرقم: <RgField g={g} field="number" placeholder="مثال: 6001" width={80} /></span> : <span style={{ fontSize: 11, color: "#64748b" }}>الرقم: {g.number || "—"}</span>}
              {rgLocal && <span style={{ fontSize: 10.5, color: "#94a3b8" }}>(معرّف السنترال {g.id})</span>}
              {g.ring_strategy && <span style={{ fontSize: 11, color: "#64748b" }}>({{ ring_all: "الكل معًا", sequentially: "بالتتابع", memory_hunt: "تراكمي", custom: "مخصص" }[g.ring_strategy] || g.ring_strategy})</span>}
              {busy === `rg${g.id}` && <span style={{ fontSize: 11, color: "#0369a1" }}>جارٍ الحفظ…</span>}
            </div>
            {g.custom ? (
              <div style={{ fontSize: 11.5, color: "#92400e" }}>
                استراتيجية مخصصة (مراحل زمنية) — الأعضاء الحاليون: {g.members.map((m) => m.text2 || m.text || m.value).join("، ") || "—"} · تعديلها من واجهة السنترال
              </div>
            ) : rgLocal ? ( /* v3.4.807 — سجل محلي: الأعضاء للعرض فقط (لا مربعات) — التعديل الفعلي من واجهة السنترال */
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                {g.members.length ? g.members.map((m) => {
                  const x = exts.find((e) => String(e.id) === String(m.value) || (m.text2 && String(e.number) === String(m.text2)));
                  const label = x ? `${x.number}${x.name && x.name !== x.number ? ` ${x.name}` : ""}` : (m.text2 || m.text || m.value);
                  return <span key={m.value + ":" + m.text2} style={{ fontSize: 11.5, background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "3px 10px", color: "#0f766e", fontWeight: 700 }}>{label}</span>;
                }) : <span style={{ fontSize: 11.5, color: "#94a3b8" }}>لا أعضاء مسجلون — اضغط «تحديث من سجل المكالمات» أو عدّل في واجهة السنترال</span>}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {exts.map((x) => {
                  const on = g.members.some((m) => m.type === "extension" && (String(m.value) === String(x.id) || String(m.text2) === String(x.number)));
                  return (
                    <label key={x.id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: on ? "#0f766e" : "#64748b", cursor: isAdmin ? "pointer" : "default", background: on ? "#f0fdfa" : "#f8fafc", border: `1px solid ${on ? "#99f6e4" : "#e2e8f0"}`, borderRadius: 8, padding: "3px 8px" }}>
                      <input type="checkbox" disabled={!isAdmin || busy === `rg${g.id}`} checked={on} onChange={(e) => toggleRgMember(g, x, e.target.checked)} />
                      {x.number}{x.name && x.name !== x.number ? ` ${x.name}` : ""}
                    </label>
                  );
                })}
                {g.members.some((m) => m.type !== "extension") && <span style={{ fontSize: 11, color: "#92400e" }}>+ مجموعات أقسام معرفة في السنترال</span>}
              </div>
            )}
          </div>
        )) : <div style={{ padding: 14, textAlign: "center", color: rgErr ? "#991b1b" : "#94a3b8", fontSize: 12.5 }}>{rgErr || "لا توجد مجموعات رنين معرفة في السنترال"}</div>}
      </>))}
      {box("📤 المسارات الصادرة — من يحق له الخروج من كل شريحة", (
        data.outbound.map((r) => (
          <div key={r.id} style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 10, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
              <span style={{ fontWeight: 800, fontSize: 13 }}>{r.name}</span>
              <span style={{ fontSize: 11, color: "#64748b" }}>عبر: {r.trunks.join("، ") || "—"}</span>
              <span style={{ fontSize: 11, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>📱 <PhoneCell k={`outbound:${r.id}`} /></span>
              {busy === `out${r.id}` && <span style={{ fontSize: 11, color: "#0369a1" }}>جارٍ الحفظ…</span>}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {exts.map((x) => {
                const on = r.ext_list.some((e) => String(e.ext_number) === String(x.number) || String(e.id) === String(x.id));
                return (
                  <label key={x.id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: on ? "#0f766e" : "#64748b", cursor: isAdmin ? "pointer" : "default", background: on ? "#f0fdfa" : "#f8fafc", border: `1px solid ${on ? "#99f6e4" : "#e2e8f0"}`, borderRadius: 8, padding: "3px 8px" }}>
                    <input type="checkbox" disabled={!isAdmin || busy === `out${r.id}`} checked={on} onChange={(e) => toggleOutExt(r, x, e.target.checked)} />
                    {x.number}{x.name && x.name !== x.number ? ` ${x.name}` : ""}
                  </label>
                );
              })}
              {r.ext_list.some((e) => e.type !== "extension") && <span style={{ fontSize: 11, color: "#92400e" }}>+ مجموعات/أقسام معرفة في السنترال</span>}
            </div>
          </div>
        ))
      ))}
    </div>
  );
}

// ── سجل المكالمات ──
function CdrTab({ myExt }) {
  // v3.4.182 — بلا فلتر تاريخ (الأحدث أولًا)؛ فلتر الجهة المستقبلة: all | ext (خارجية) | int (داخلية) | rg:<رقم> | x:<تحويلة>
  const [recv, setRecv] = useState("all");
  const [rgs, setRgs] = useState([]);
  const [exts, setExts] = useState([]);
  const [accts, setAccts] = useState([]);
  useEffect(() => {
    yeastar.ringGroups().then((r) => setRgs(r.data?.groups || [])).catch(() => {});
    yeastar.extensions().then((r) => setExts(r.data?.extensions || [])).catch(() => {});
    portalAdmin.directory().then((r) => setAccts(r.data?.accounts || [])).catch(() => {}); // v3.4.201 — يعمل لغير الأدمن
  }, []);
  const acctOf = (num) => accts.find((a) => String(a.yeastar_extension || "").trim() === String(num || ""));
  const [statusF, setStatusF] = useState("");
  const [numF, setNumF] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null); // v3.3.926 — معرف المكالمة التي يُستمع لتسجيلها
  const ctl = useTableCtl([
    { key: "time", label: "الوقت", get: (r) => r.time, sortGet: (r) => timeKey(r.time) },
    { key: "from", label: "من", get: (r) => `${r.from_number} ${r.from_name || ""} ${r.from_match ? r.from_match.kind + " " + r.from_match.name : ""}` },
    { key: "to", label: "إلى", get: (r) => `${r.to_number} ${r.to_name || ""} ${r.to_match ? r.to_match.kind + " " + r.to_match.name : ""}` },
    { key: "ans", label: "من استقبل (مجموعة الرنين)", get: (r) => (r.answered_by ? `${r.answered_by.number} ${r.answered_by.name || ""} ${acctOf(r.answered_by.number)?.display_name || ""}` : (r.to_is_rg ? "لم يُجب أحد" : "")) }, // v3.4.182
    { key: "type", label: "النوع", get: (r) => (CTYPE[r.call_type] || [r.call_type || ""])[0] },
    { key: "st", label: "الحالة", get: (r) => (DISPO[r.disposition] || [r.disposition || ""])[0] },
    { key: "trunk", label: "الخط/الشريحة", get: (r) => r.src_trunk || r.dst_trunk || "" }, // v3.3.929
    { key: "dur", label: "المدة", get: (r) => r.duration, num: true },
    { key: "rec", label: "التسجيل", get: (r) => (r.record_file ? "يوجد" : "بدون") },
    { key: "act", label: "", get: () => "" },
  ], data?.rows || []);
  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: (recv === "ext" || recv === "int") ? 100 : 50 }; // v3.4.182 — بلا تاريخ: الأحدث أولًا
    if (recv.startsWith("rg:") || recv.startsWith("x:")) params.to = recv.split(":")[1]; // الجهة المستقبلة
    if (statusF) params.status = statusF;
    if (numF.trim()) params.number = numF.trim();
    yeastar.cdr(params).then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setData({ rows: [], total_number: 0 }); }).finally(() => setLoading(false));
  }, [recv, statusF, numF, page]);
  useEffect(() => { load(); }, [load]);
  // v3.4.182 — الخارجية/الداخلية تُصفّى محليًا حسب نوع المكالمة؛ الباقي من السنترال (call_to)
  const visibleRows = (data?.rows || []).filter((r) => recv === "ext" ? r.call_type === "Outbound" : recv === "int" ? r.call_type === "Internal" : true);
  const rgSeen = {}; for (const r of (data?.rows || [])) if (r.to_is_rg && r.to_number) rgSeen[r.to_number] = r.to_name || r.to_number;
  for (const g of rgs) if (g.number) rgSeen[String(g.number)] = g.name || rgSeen[String(g.number)] || String(g.number);
  const linkedExts = exts.filter((x) => acctOf(x.number));
  const marketerExts = (linkedExts.length ? linkedExts : exts.filter((x) => x.name && x.name !== String(x.number))).slice().sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true }));
  const recvChips = [["all", "📋 الكل"], ["ext", "🌐 الخارجية"], ["int", "🏢 الداخلية"],
    ...Object.entries(rgSeen).map(([num, name]) => [`rg:${num}`, `🔔 ${name}`]),
    ...marketerExts.map((x) => [`x:${x.number}`, `👤 ${acctOf(x.number)?.display_name || x.name} · ${x.number}`])];
  const party = (num, name, match) => (
    <div>
      <div style={{ direction: "ltr", textAlign: "right", fontWeight: 700 }}>{num || "—"}</div>
      {match
        ? <span style={{ background: (KIND_COLOR[match.kind] || [])[0], color: (KIND_COLOR[match.kind] || [])[1], borderRadius: 8, padding: "1px 8px", fontSize: 10.5, fontWeight: 800 }}>{match.kind}: {match.name}</span>
        : name ? <div style={{ fontSize: 10.5, color: "#64748b" }}>{name}</div> : null}
    </div>
  );
  const totalPages = Math.max(1, Math.ceil((data?.total_number || 0) / 50));
  return (
    <div>
      {/* v3.4.182 — فلتر الجهة المستقبلة للاتصال */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: "#334155" }}>الجهة المستقبلة:</span>
        {recvChips.map(([k, l]) => (
          <button key={k} onClick={() => { setRecv(k); setPage(1); }}
            style={{ padding: "5px 12px", borderRadius: 12, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: recv === k ? 800 : 600,
              border: recv === k ? "2px solid #0369a1" : "1px solid #e2e8f0", background: recv === k ? "#f0f9ff" : "#fff", color: recv === k ? "#0369a1" : "#64748b" }}>{l}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select value={statusF} onChange={(e) => { setStatusF(e.target.value); setPage(1); }} style={inputSt}>
          <option value="">كل الحالات</option><option value="ANSWERED">مجابة</option><option value="NO ANSWER">بلا رد</option>
          <option value="BUSY">مشغول</option><option value="FAILED">فاشلة</option><option value="VOICEMAIL">بريد صوتي</option>
        </select>
        <input value={numF} onChange={(e) => setNumF(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
          placeholder="بحث برقم…" dir="ltr" style={{ ...inputSt, width: 140 }} />
        <button onClick={() => { setPage(1); load(); }} style={btnSt()}>{loading ? "…" : "بحث"}</button>
        {data && <span style={{ fontSize: 11.5, color: "#64748b" }}>الإجمالي: {data.total_number}</span>}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          {ctl.header}
          <tbody>
            {ctl.apply(visibleRows).map((r, i) => {
              const external = r.call_type === "Inbound" ? r.from_number : r.to_number;
              const rowKey = r.id || `row${i}`;
              return (
                <React.Fragment key={rowKey}>
                <tr>
                  <td style={{ ...td, whiteSpace: "nowrap", fontSize: 11.5, color: "#475569" }}>{r.time}</td>
                  <td style={td}>{party(r.from_number, r.from_name, r.from_match)}</td>
                  <td style={td}>{party(r.to_number, r.to_name, r.to_match)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{/* v3.4.182 — من استقبل مكالمة مجموعة الرنين */}
                    {r.to_is_rg ? (r.answered_by
                      ? <div><div style={{ direction: "ltr", textAlign: "right", fontWeight: 800 }}>{r.answered_by.number}</div><div style={{ fontSize: 10.5, color: "#0f766e", fontWeight: 700 }}>{acctOf(r.answered_by.number)?.display_name || r.answered_by.name || ""}</div></div>
                      : <span style={{ fontSize: 11, color: "#92400e" }}>لم يُجب أحد</span>) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={td}>{chip(CTYPE[r.call_type] || [r.call_type || "—", "#f1f5f9", "#64748b"])}</td>
                  <td style={td}>{chip(DISPO[r.disposition] || [r.disposition || "—", "#f1f5f9", "#64748b"])}</td>
                  <td style={{ ...td, fontSize: 11, color: "#475569", whiteSpace: "nowrap" }}>{r.src_trunk || r.dst_trunk || "—"}</td>
                  <td style={{ ...td, direction: "ltr", textAlign: "right" }}>{fmtDur(r.duration)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {/* v3.3.926 — استماع لتسجيل المكالمة مباشرة من السجل */}
                    {r.record_file
                      ? <button onClick={() => setPlaying(playing === rowKey ? null : rowKey)}
                          style={{ ...btnSt(playing === rowKey ? "#991b1b" : "#0369a1"), padding: "3px 10px", fontSize: 11 }}>
                          {playing === rowKey ? "✕ إغلاق" : "▶ استماع"}
                        </button>
                      : <span style={{ fontSize: 10.5, color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={td}>{r.call_type !== "Internal" && <DialBtn number={external} myExt={myExt} small />}</td>
                </tr>
                {playing === rowKey && r.record_file && (
                  <tr><td colSpan={10} style={{ padding: "6px 12px", background: "#f8fafc" }}>
                    <audio controls autoPlay src={yeastar.recordingFileUrl(r.record_file)} style={{ width: "100%", maxWidth: 560 }} />
                    <a href={yeastar.recordingFileUrl(r.record_file, true)} target="_blank" rel="noreferrer"
                      style={{ ...btnSt("#64748b"), padding: "4px 10px", fontSize: 11, textDecoration: "none", marginInlineStart: 10 }}>تنزيل</a>
                  </td></tr>
                )}
                </React.Fragment>
              );
            })}
            {data && !visibleRows.length && <tr><td colSpan={10} style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>{loading ? "جارٍ التحميل…" : "لا توجد مكالمات في هذا النطاق"}</td></tr>}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...btnSt("#64748b"), opacity: page <= 1 ? 0.5 : 1 }}>السابق</button>
          <span style={{ fontSize: 12.5, alignSelf: "center", color: "#475569" }}>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ ...btnSt("#64748b"), opacity: page >= totalPages ? 0.5 : 1 }}>التالي</button>
        </div>
      )}
    </div>
  );
}

// ── التسجيلات ──
function RecordingsTab() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(null); // id قيد التشغيل
  const ctl = useTableCtl([
    { key: "time", label: "الوقت", get: (r) => r.time, sortGet: (r) => timeKey(r.time) },
    { key: "from", label: "من", get: (r) => `${r.call_from_name || ""} ${r.call_from_number || ""}` },
    { key: "to", label: "إلى", get: (r) => `${r.call_to_name || ""} ${r.call_to_number || ""}` },
    { key: "type", label: "النوع", get: (r) => (CTYPE[r.call_type] || [r.call_type || ""])[0] },
    { key: "dur", label: "المدة", get: (r) => r.duration, num: true },
    { key: "size", label: "الحجم", get: (r) => r.size || 0, num: true },
    { key: "act", label: "استماع / تنزيل", get: () => "", filter: false },
  ], data?.rows || []);
  const load = useCallback(() => {
    yeastar.recordings({ page, page_size: 50 }).then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setData({ rows: [], total_number: 0 }); });
  }, [page]);
  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil((data?.total_number || 0) / 50));
  return (
    <div>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          {ctl.header}
          <tbody>
            {ctl.apply(data?.rows || []).map((r) => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={{ ...td, whiteSpace: "nowrap", fontSize: 11.5, color: "#475569" }}>{r.time}</td>
                  <td style={td}><span style={{ fontWeight: 700 }}>{r.call_from_name || ""}</span> <span style={{ direction: "ltr", display: "inline-block" }}>{r.call_from_number}</span></td>
                  <td style={td}><span style={{ fontWeight: 700 }}>{r.call_to_name || ""}</span> <span style={{ direction: "ltr", display: "inline-block" }}>{r.call_to_number}</span></td>
                  <td style={td}>{chip(CTYPE[r.call_type] || [r.call_type || "—", "#f1f5f9", "#64748b"])}</td>
                  <td style={{ ...td, direction: "ltr", textAlign: "right" }}>{fmtDur(r.duration)}</td>
                  <td style={{ ...td, fontSize: 11.5, color: "#64748b" }}>{r.size ? `${(r.size / 1048576).toFixed(1)} MB` : "—"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button onClick={() => setPlaying(playing === r.id ? null : r.id)} style={{ ...btnSt("#0369a1"), padding: "4px 10px", fontSize: 11, marginInlineEnd: 6 }}>{playing === r.id ? "إغلاق" : "▶ استماع"}</button>
                    <a href={yeastar.recordingUrl(r.id, true)} target="_blank" rel="noreferrer" style={{ ...btnSt("#64748b"), padding: "4px 10px", fontSize: 11, textDecoration: "none" }}><FiDownload size={11} /> تنزيل</a>
                  </td>
                </tr>
                {playing === r.id && (
                  <tr><td colSpan={7} style={{ padding: "6px 12px", background: "#f8fafc" }}>
                    {/* البث عبر بروكسي الخادم — الكوكي نفس النطاق فيمر التحقق */}
                    <audio controls autoPlay src={yeastar.recordingUrl(r.id)} style={{ width: "100%", maxWidth: 560 }} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {data && !data.rows.length && <tr><td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>لا توجد تسجيلات</td></tr>}
            {!data && <tr><td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</td></tr>}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...btnSt("#64748b"), opacity: page <= 1 ? 0.5 : 1 }}>السابق</button>
          <span style={{ fontSize: 12.5, alignSelf: "center", color: "#475569" }}>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ ...btnSt("#64748b"), opacity: page >= totalPages ? 0.5 : 1 }}>التالي</button>
        </div>
      )}
    </div>
  );
}

// ── v3.3.934 — الرسائل SMS عبر جايتواي TG800: إرسال + وارد حي + USSD + إعدادات ──
function SmsTab({ isAdmin }) {
  const PORTS = [1, 2, 3, 4, 5, 6, 7, 8];
  const [cfg, setCfg] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [portF, setPortF] = useState("");
  const [sub, setSub] = useState("inbox"); // inbox | outbox | ussd | tgset
  const [f, setF] = useState({ port: 1, destination: "", content: "" });
  const [busy, setBusy] = useState(false);
  const [ussd, setUssd] = useState({ port: 1, content: "*166#" });
  const [ussdRes, setUssdRes] = useState("");
  const [showWebPass, setShowWebPass] = useState(false); // v3.4.396
  const copyTxt = (t, label) => { try { navigator.clipboard.writeText(String(t || "")); toast.success(`نُسخ ${label}`); } catch { toast.error("تعذّر النسخ"); } };
  const load = useCallback(() => {
    tgSms.settings().then((r) => {
      setCfg(r.data);
    }).catch(() => {});
    tgSms.inbox({ page, ...(q ? { q } : {}), ...(portF ? { port: portF } : {}) }).then((r) => setInbox(r.data)).catch((e) => { toast.error(e.response?.data?.error || "فشل تحميل الوارد"); setInbox({ rows: [], total: 0 }); });
    tgSms.outbox().then((r) => setOutbox(r.data?.rows || [])).catch(() => {});
  }, [page, q, portF]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!f.destination.trim() || !f.content.trim()) return toast.error("الرقم والنص مطلوبان");
    setBusy(true);
    try { await tgSms.send(f); toast.success(`أُرسلت الرسالة من المنفذ ${f.port} ✓`); setF({ ...f, destination: "", content: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الإرسال"); }
    setBusy(false);
  };
  const runUssd = async () => {
    setBusy(true); setUssdRes("");
    try { const r = await tgSms.ussd(ussd); setUssdRes(r.data?.response || "تم الإرسال — الرد قد يصل كرسالة واردة"); }
    catch (e) { setUssdRes(e.response?.data?.error || "فشل"); }
    setBusy(false);
  };
  const notConfigured = cfg && !cfg.base_url;
  const totalPages = Math.max(1, Math.ceil((inbox?.total || 0) / 50));
  const subBtn = (k, l) => (
    <button key={k} onClick={() => setSub(k)} style={{ padding: "5px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: sub === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
      border: sub === k ? "2px solid #0369a1" : "1px solid #e2e8f0", background: sub === k ? "#f0f9ff" : "#fff", color: sub === k ? "#0369a1" : "#64748b" }}>{l}</button>
  );
  return (
    <div>
      {cfg && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          {notConfigured
            ? <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 12, padding: "3px 12px", fontSize: 11.5, fontWeight: 800 }}>الجايتواي غير مضبوط — من تبويب إعدادات الجايتواي بالأسفل</span>
            : <span style={{ background: /✓/.test(cfg.socket_state || "") ? "#dcfce7" : "#fef3c7", color: /✓/.test(cfg.socket_state || "") ? "#166534" : "#92400e", borderRadius: 12, padding: "3px 12px", fontSize: 11.5, fontWeight: 800 }}>استقبال الوارد: {inbox?.socket_state || cfg.socket_state || "—"}</span>}
        </div>
      )}
      {/* v3.4.396 — لوحة الجايتواي (واجهة الويب): رابط + بيانات الدخول */}
      {cfg && (cfg.web_url || isAdmin) && (
        <div style={{ background: "linear-gradient(135deg,#f0f9ff,#eff6ff)", border: "1px solid #bfdbfe", borderRadius: 12, padding: "12px 14px", marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 20 }}>🖥️</span><div><div style={{ fontSize: 13.5, fontWeight: 900, color: "#0c4a6e" }}>لوحة الجايتواي (واجهة الويب)</div><div style={{ fontSize: 11, color: "#475569" }}>لفتح صندوق الرسائل على الجهاز مباشرة (الرسائل القديمة/التي لا تصل عبر الـAPI)</div></div></div>
          {cfg.web_url ? (<>
            <a href={cfg.web_url} target="_blank" rel="noreferrer" style={{ padding: "9px 16px", borderRadius: 10, background: "#0369a1", color: "#fff", fontWeight: 900, fontSize: 12.5, textDecoration: "none", whiteSpace: "nowrap" }}>🔗 فتح لوحة الجايتواي ↗</a>
            <span dir="ltr" style={{ fontSize: 11.5, color: "#64748b", fontFamily: "monospace" }}>{cfg.web_url}</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginInlineStart: "auto", alignItems: "center" }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: "#334155" }}>المستخدم:</span>
              <code style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "5px 10px", fontSize: 12.5, direction: "ltr" }}>{cfg.web_user || "—"}</code>
              <button onClick={() => copyTxt(cfg.web_user, "اسم المستخدم")} title="نسخ" style={{ padding: "5px 9px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 11.5 }}>📋</button>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: "#334155", marginInlineStart: 6 }}>كلمة المرور:</span>
              <code style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "5px 10px", fontSize: 12.5, direction: "ltr", minWidth: 70, textAlign: "center" }}>{cfg.web_pass ? (showWebPass ? cfg.web_pass : "••••••••") : "—"}</code>
              {cfg.web_pass && <button onClick={() => setShowWebPass(v => !v)} title={showWebPass ? "إخفاء" : "إظهار"} style={{ padding: "5px 9px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 11.5 }}>{showWebPass ? "🙈" : "👁"}</button>}
              {cfg.web_pass && <button onClick={() => copyTxt(cfg.web_pass, "كلمة المرور")} title="نسخ" style={{ padding: "5px 9px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 11.5 }}>📋</button>}
            </div>
          </>) : <span style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", borderRadius: 999, padding: "4px 12px", fontWeight: 700 }}>لم يُضبط رابط اللوحة بعد — أضِفه من «⚙️ إعدادات الجايتواي» (قسم لوحة الويب)</span>}
        </div>
      )}
      {/* الإرسال */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 10 }}>✉️ إرسال رسالة SMS</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
          <select value={f.port} onChange={(e) => setF({ ...f, port: +e.target.value })} style={{ ...inputSt, width: 120 }}>
            {PORTS.map((p) => <option key={p} value={p}>شريحة منفذ {p}</option>)}
          </select>
          <input value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} placeholder="05xxxxxxxx" dir="ltr" style={{ ...inputSt, width: 150 }} />
          <textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} placeholder="نص الرسالة…" rows={2}
            style={{ ...inputSt, flex: 1, minWidth: 220, resize: "vertical", fontFamily: "inherit" }} />
          <button onClick={send} disabled={busy} style={btnSt("#0f766e")}>{busy ? "…" : "📤 إرسال"}</button>
        </div>
        <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 6 }}>حد الجايتواي: رسالة واحدة كل 10 ثوانٍ لكل منفذ.</div>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {subBtn("inbox", `📥 الوارد (${inbox?.total ?? "…"})`)}
        {subBtn("outbox", "📤 الصادر")}
        {subBtn("ussd", "#️⃣ أوامر USSD")}
        {isAdmin && subBtn("tgset", "⚙️ إعدادات الجايتواي")}
      </div>
      {sub === "inbox" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <select value={portF} onChange={(e) => { setPortF(e.target.value); setPage(1); }} style={inputSt}>
              <option value="">كل المنافذ</option>{PORTS.map((p) => <option key={p} value={p}>منفذ {p}</option>)}
            </select>
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="بحث بالرقم أو النص…" style={{ ...inputSt, width: 200 }} />
            <button onClick={load} style={btnSt("#64748b")}>تحديث</button>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr>{["المنفذ", "المرسل", "النص", "وقت الاستلام", ""].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {(inbox?.rows || []).map((m) => (
                  <tr key={m.id}>
                    <td style={{ ...td, fontWeight: 700 }}>{m.port || "—"}</td>
                    <td style={{ ...td, direction: "ltr", textAlign: "right", fontWeight: 700 }}>{m.sender}</td>
                    <td style={{ ...td, whiteSpace: "pre-wrap", maxWidth: 420 }}>{m.content}</td>
                    <td style={{ ...td, fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{m.recv_time || String(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td style={td}>{isAdmin && <button onClick={async () => { if (window.confirm("حذف الرسالة؟")) { await tgSms.removeInbox(m.id).catch(() => toast.error("فشل")); load(); } }}
                      style={{ ...btnSt("#991b1b"), padding: "3px 8px", fontSize: 10.5 }}>حذف</button>}</td>
                  </tr>
                ))}
                {inbox && !inbox.rows.length && <tr><td colSpan={5} style={{ padding: 26, textAlign: "center", color: "#94a3b8" }}>
                  لا يوجد وارد بعد — الرسائل الجديدة تظهر هنا فور وصولها للجايتواي (الرسائل القديمة الموجودة في صندوق الجايتواي لا يتيحها الـAPI)
                </td></tr>}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "center" }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...btnSt("#64748b"), opacity: page <= 1 ? 0.5 : 1 }}>السابق</button>
              <span style={{ fontSize: 12.5, alignSelf: "center", color: "#475569" }}>{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ ...btnSt("#64748b"), opacity: page >= totalPages ? 0.5 : 1 }}>التالي</button>
            </div>
          )}
        </div>
      )}
      {sub === "outbox" && (
        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead><tr>{["المنفذ", "إلى", "النص", "الحالة", "أرسلها", "الوقت"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {outbox.map((m) => (
                <tr key={m.id}>
                  <td style={{ ...td, fontWeight: 700 }}>{m.port}</td>
                  <td style={{ ...td, direction: "ltr", textAlign: "right" }}>{m.destination}</td>
                  <td style={{ ...td, whiteSpace: "pre-wrap", maxWidth: 420 }}>{m.content}</td>
                  <td style={td}>{chip(m.status === "أُرسلت" ? [m.status, "#dcfce7", "#166534"] : [m.status, "#fee2e2", "#991b1b"])}</td>
                  <td style={{ ...td, fontSize: 11.5, color: "#64748b" }}>{m.created_by}</td>
                  <td style={{ ...td, fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{String(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {!outbox.length && <tr><td colSpan={6} style={{ padding: 26, textAlign: "center", color: "#94a3b8" }}>لا يوجد صادر بعد</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {sub === "ussd" && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, maxWidth: 560 }}>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>أوامر المشغل مثل الاستعلام عن الرصيد (<span dir="ltr">*166#</span> لـSTC) أو الباقات — تُنفذ على شريحة المنفذ المختار.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={ussd.port} onChange={(e) => setUssd({ ...ussd, port: +e.target.value })} style={{ ...inputSt, width: 120 }}>
              {PORTS.map((p) => <option key={p} value={p}>منفذ {p}</option>)}
            </select>
            <input value={ussd.content} onChange={(e) => setUssd({ ...ussd, content: e.target.value })} dir="ltr" style={{ ...inputSt, width: 140 }} />
            <button onClick={runUssd} disabled={busy} style={btnSt()}>{busy ? "…" : "تنفيذ"}</button>
          </div>
          {ussdRes && <pre style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginTop: 10, fontSize: 12, whiteSpace: "pre-wrap", direction: "ltr", textAlign: "left" }}>{ussdRes}</pre>}
        </div>
      )}
      {sub === "tgset" && isAdmin && <TgGatewaySettingsForm onSaved={load} />}{/* v3.4.397 — النموذج مكوّن مستقل */}
    </div>
  );
}

// ── v3.4.397 — نموذج إعدادات الجايتواي TG (مستقل): يُستخدم في تبويب الرسائل وفي الإعدادات ← «ربط الجايتواي (SMS)» ──
function TgGatewaySettingsForm({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tgf, setTgf] = useState({ base_url: "", api_user: "apiuser", api_pass: "", socket_port: 5038, web_url: "", web_user: "", web_pass: "" });
  const load = useCallback(() => {
    tgSms.settings().then((r) => {
      setCfg(r.data);
      setTgf((p) => ({ ...p, base_url: r.data.base_url || "", api_user: r.data.api_user || "apiuser", socket_port: r.data.socket_port || 5038, api_pass: "", web_url: r.data.web_url || "", web_user: r.data.web_user || "", web_pass: r.data.web_pass || "" }));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const saveTg = async () => {
    setBusy(true);
    try { await tgSms.saveSettings(tgf); toast.success("حُفظت إعدادات الجايتواي ✓"); load(); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  return (
        <div style={{ maxWidth: 620 }}>
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 12, marginBottom: 12, fontSize: 12, color: "#1e40af", lineHeight: 1.8 }}>
            <b>خطوات التفعيل:</b> في واجهة الجايتواي TG800 → SMS → API Settings → فعّل <b>Enable API</b> وحدد اسم مستخدم وكلمة مرور قوية (غيّر الافتراضية apiuser/apipass) واترك Permitted IP فارغًا.<br />
            <b>مهم:</b> الجايتواي داخلي (192.168.100.254) — يحتاج Port Forwarding من الراوتر لمنفذين: منفذ الويب (80) لمنفذ خارجي مخصص، ومنفذ الـAPI (5038). ضع هنا الرابط العام (مثال <span dir="ltr">http://IP-العام:8090</span>).
          </div>
          {[["رابط الجايتواي العام (مع المنفذ)", "base_url", { placeholder: "http://x.x.x.x:8090", dir: "ltr" }],
            ["اسم مستخدم الـAPI", "api_user", { dir: "ltr" }],
            [`كلمة مرور الـAPI ${cfg?.has_pass ? "(محفوظة — اتركها فارغة للإبقاء)" : ""}`, "api_pass", { type: "password", dir: "ltr" }],
            ["منفذ الـSocket (استقبال الوارد)", "socket_port", { dir: "ltr" }],
            ["🖥️ رابط لوحة الجايتواي (واجهة الويب العامة — تظهر لفريق الكول سنتر في تبويب الرسائل)", "web_url", { placeholder: "http://x.x.x.x:8090", dir: "ltr" }], /* v3.4.396 */
            ["اسم مستخدم لوحة الويب", "web_user", { dir: "ltr", placeholder: "admin" }],
            ["كلمة مرور لوحة الويب (تُعرض لفريق الكول سنتر)", "web_pass", { dir: "ltr" }]].map(([label, key, props]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: "#334155", marginBottom: 3 }}>{label}</div>
              <input value={tgf[key]} onChange={(e) => setTgf({ ...tgf, [key]: e.target.value })} style={{ ...inputSt, width: "100%", boxSizing: "border-box" }} {...props} />
            </div>
          ))}
          <button onClick={saveTg} disabled={busy} style={btnSt("#0f766e")}>{busy ? "جارٍ الحفظ…" : "💾 حفظ وإعادة الاتصال"}</button>
        </div>
  );
}

// ── v3.3.939 — واتساب بزنس: استعراض المحادثات لكل الأرقام المربوطة بميتا ──
function WaBizTab({ isAdmin, scope = "marketing", hideNumbers = false }) { // v3.4.743 — hideNumbers: الشاشة الموحّدة ترسم بطاقات الأرقام بنفسها // v3.4.180 — scope: marketing (بدون الرقم الرئيسي) | company (الرقم الرئيسي فقط)
  const { user: bzUser } = useAuth(); // v3.4.114 — أعلام تفصيلية للتبويبات
  const isCompany = scope === "company" || scope === "daleelak"; // v3.4.587 — صفحة رقم رسمي واحد (الرئيسي أو دليلك)
  const isSingle = isCompany;
  const [numQ, setNumQ] = useState(""); // v3.4.587 — بحث في الأرقام
  const canReply = isAdmin || !!bzUser?.bizchat_can_reply;
  const canMarketers = isAdmin || !!bzUser?.bizchat_can_view_marketers;
  const canMeta = isAdmin || !!bzUser?.bizchat_can_meta;
  const [wtab, setWtab] = useState("chats"); // v3.3.948 — chats (المحادثات) | meta (إعدادات ميتا)
  const [metaSub, setMetaSub] = useState(null); // v3.4.178 — داخل إعدادات ميتا: settings | marketers
  const [nums, setNums] = useState([]);
  const [biz, setBiz] = useState("all");
  const [q, setQ] = useState("");
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null); // رقم العميل المفتوح
  const [thread, setThread] = useState(null);
  // v3.4.161 — تحويل المحادثة إلى مسوّق (كما في تطبيق المنظم التسويقي): قائمة المؤهلين + المحوّل له للخيط المفتوح
  const [assignees, setAssignees] = useState([]);
  const [threadAssignee, setThreadAssignee] = useState(null);
  const [assigning, setAssigning] = useState(false);
  useEffect(() => {
    if (!biz || biz === "all" || biz === "غير محدد") { setAssignees([]); return; }
    waBiz.assignees(biz).then((r) => setAssignees(r.data?.assignees || [])).catch(() => setAssignees([]));
  }, [biz]);
  const assignTo = async (portalUserId) => {
    if (!active || !biz || biz === "all") return;
    setAssigning(true);
    try {
      const r = await waBiz.assign({ biz, phone: active, portal_user_id: portalUserId || null });
      setThreadAssignee(r.data?.assignee || null);
      toast.success(r.data?.assignee ? `حُوّلت المحادثة إلى ${r.data.assignee.name}` : "أُلغي تحويل المحادثة");
      loadConvs();
      waBiz.assignees(biz).then((x) => setAssignees(x.data?.assignees || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.error || "فشل التحويل"); }
    setAssigning(false);
  };
  const loadNums = useCallback(() => {
    waBiz.numbers({ scope }).then((r) => {
      const list = r.data?.numbers || []; setNums(list);
      if (scope === "company") { const main = list.find((n) => n.is_company_main) || (r.data?.company_main ? { display_number: r.data.company_main } : null); if (main) setBiz(main.display_number); } // v3.4.180
      if (scope === "daleelak") { const dl = list.find((n) => n.is_daleelak) || (r.data?.daleelak ? { display_number: r.data.daleelak } : null); if (dl) setBiz(dl.display_number); } // v3.4.587
    }).catch((e) => toast.error(e.response?.data?.error || "فشل تحميل الأرقام"));
  }, [scope]);
  const [restricted, setRestricted] = useState(null); // v3.4.841 — {restricted, linked}: يرى المحوّل إليه فقط
  const loadConvs = useCallback(() => {
    waBiz.conversations({ biz, scope, ...(q.trim() ? { q: q.trim() } : {}) })
      .then((r) => { setConvs(r.data?.conversations || []); setRestricted(r.data?.restricted ? { linked: r.data.linked !== false } : null); })
      .catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setConvs([]); });
  }, [biz, q, scope]);
  useEffect(() => { loadNums(); }, [loadNums]);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  const openThread = (phone) => {
    setActive(phone); setThread(null);
    waBiz.thread(phone, biz).then((r) => { setThread(r.data?.messages || []); setThreadAssignee(r.data?.assignee || null); loadNums(); loadConvs(); }) // v3.4.067 / v3.4.161
      .catch((e) => toast.error(e.response?.data?.error || "فشل فتح المحادثة"));
  };
  // v3.4.038 — الرد النصي من الخيط (يُرسل من نفس رقم الأعمال المستقبِل)
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const sendReply = async () => {
    const body = replyText.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      await waBiz.reply({ phone: active, body, biz }); // v3.4.067 — يُرسل من الرقم المختار
      setReplyText("");
      const r = await waBiz.thread(active, biz);
      setThread(r.data?.messages || []); loadConvs();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الإرسال", { duration: 9000 }); }
    setSending(false);
  };
  const fmtT = (t) => String(t || "").slice(0, 16).replace("T", " ");
  const ST = { pending: "⏳", sent: "✓", delivered: "✓✓", read: "✓✓🔵", failed: "✗" };
  const META_LINKS = [
    ["📞 أرقام الهاتف (WhatsApp Manager)", "https://business.facebook.com/latest/whatsapp_manager/phone_numbers"],
    ["📋 قوالب الرسائل", "https://business.facebook.com/latest/whatsapp_manager/message_templates"],
    ["🏢 حسابات واتساب للأعمال (WABA)", "https://business.facebook.com/settings/whatsapp-business-accounts"],
    ["🧩 تطبيقات المطورين (الويبهوك)", "https://developers.facebook.com/apps"],
    ["🧪 Graph API Explorer (الاشتراكات)", "https://developers.facebook.com/tools/explorer"],
    ["👥 الأشخاص والصلاحيات", "https://business.facebook.com/settings/people"],
  ];
  return (
    <div>
      {/* v3.3.948 — تبويبان: المحادثات / إعدادات ميتا — v3.4.180: تُخفى في صفحة الرقم الرئيسي */}
      {!isCompany && (
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[["chats", "💬 المحادثات"], ...((canMeta || canMarketers) ? [["meta", "⚙️ إعدادات ميتا"]] : [])].map(([k, l]) => ( // v3.4.110 — واتس المسوقين — v3.4.114: حسب الأعلام — v3.4.178: واتس المسوقين تاب فرعي داخل إعدادات ميتا
          <button key={k} onClick={() => setWtab(k)}
            style={{ padding: "6px 18px", borderRadius: 10, fontSize: 13, fontWeight: wtab === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
              border: wtab === k ? "2px solid #16a34a" : "1px solid #e2e8f0", background: wtab === k ? "#f0fdf4" : "#fff", color: wtab === k ? "#16a34a" : "#64748b" }}>{l}</button>
        ))}
      </div>
      )}
      {/* v3.4.178 — إعدادات ميتا: تابان فرعيان (الإعدادات / واتس المسوقين) */}
      {wtab === "meta" && (canMeta || canMarketers) && (() => {
        const sub = metaSub || (canMeta ? "settings" : "marketers");
        return (
        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", paddingInlineStart: 8, borderInlineStart: "3px solid #bbf7d0" }}>
            {[...(canMeta ? [["settings", "⚙️ إعدادات ميتا"]] : []), ...(canMarketers ? [["marketers", "📱 واتس المسوقين"]] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setMetaSub(k)}
                style={{ padding: "5px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: sub === k ? 800 : 600, cursor: "pointer", fontFamily: "inherit",
                  border: sub === k ? "2px solid #16a34a" : "1px solid #e2e8f0", background: sub === k ? "#f0fdf4" : "#fff", color: sub === k ? "#16a34a" : "#64748b" }}>{l}</button>
            ))}
          </div>
          {sub === "marketers" && canMarketers && <MarketersWaTab />}
          {sub === "settings" && canMeta && (
        <div style={{ maxWidth: 900 }}>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#92400e", lineHeight: 1.8 }}>
            ميتا يمنع تضمين صفحاته (وصفحة تسجيل الدخول خصوصًا) داخل الأنظمة الأخرى لأسباب أمنية (X-Frame-Options) — لذلك تفتح هذه الروابط في تبويب متصفح مستقل، وتسجيل دخولك في ميتا يبقى محفوظًا هناك.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {META_LINKS.map(([l, u]) => (
              <a key={u} href={u} target="_blank" rel="noreferrer"
                style={{ display: "block", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", textDecoration: "none", color: "#0f172a", fontSize: 13, fontWeight: 700 }}>
                {l}
                <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 4, direction: "ltr", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.replace("https://", "")}</div>
              </a>
            ))}
          </div>
          {/* v3.4.039 — إعدادات الاتصال بميتا: التوكن الدائم يُحفظ هنا (بلا DigitalOcean) ويسري فورًا */}
          {isAdmin && <WaMetaSettings />}
         {isAdmin && <WaEmbeddedSignup isAdmin={isAdmin} />}{/* v3.4.802 */}
          {/* v3.4.032 — تشخيص «الرسائل لا تصل»: حالة كل رقم عند ميتا + أحداث الويبهوك */}
          {isAdmin && <WaBizDiagnostics />}
        </div>
          )}
        </div>
        );
      })()}
      {wtab === "chats" && <>
      {/* v3.4.587 — منتقي الأرقام: بطاقات مرتبة (الرسمية أولًا ثم غير المقروء ثم الأحدث) مع البحث والإجراءات — وفي صفحتي الرقم الرئيسي/دليلك بطاقة الرقم فقط */}
      {!hideNumbers && (() => {
        const fmtNum = (d) => { const s = String(d || ""); return /^966\d{9}$/.test(s) ? `+966 ${s.slice(3, 5)} ${s.slice(5, 8)} ${s.slice(8)}` : s; };
        const ago = (t) => { if (!t) return "—"; const d = new Date(t); if (isNaN(d)) return "—"; const now = new Date(); const same = d.toDateString() === now.toDateString(); return same ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("en-CA").slice(5); };
        const official = (n) => n.is_company_main || n.is_daleelak || /الرسمي/.test(n.name || "");
        const sorted = [...nums].sort((a, b) => (official(b) - official(a)) || ((b.unread > 0) - (a.unread > 0)) || (new Date(b.last_at || 0) - new Date(a.last_at || 0)) || ((b.total || 0) - (a.total || 0)));
        const shownNums = isSingle ? sorted.filter((n) => (scope === "daleelak" ? n.is_daleelak : n.is_company_main)).slice(0, 1) : sorted.filter((n) => !numQ.trim() || `${n.name || ""} ${n.display_number}`.toLowerCase().includes(numQ.trim().toLowerCase()));
        const totUnread = nums.reduce((s, n) => s + (Number(n.unread) || 0), 0), totMsgs = nums.reduce((s, n) => s + (Number(n.total) || 0), 0);
        const pickN = (d) => { setBiz(d); setActive(null); setThread(null); };
        const card = (key, on, onClick, icon, title, sub, meta, unread, extra) => (
          <button key={key} type="button" onClick={onClick} title={sub}
            style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit", textAlign: "start", minWidth: 0,
              border: on ? "2px solid #16a34a" : "1px solid #e2e8f0", background: on ? "linear-gradient(135deg,#f0fdf4,#ecfdf5)" : "#fff", boxShadow: on ? "0 8px 20px -12px rgba(22,163,74,.6)" : "0 1px 2px rgba(15,23,42,.04)", transition: "all .15s" }}>
            <span style={{ width: 38, height: 38, flex: "none", borderRadius: 12, display: "grid", placeItems: "center", fontSize: 16, fontWeight: 900, background: on ? "#16a34a" : "#f1f5f9", color: on ? "#fff" : "#475569" }}>{icon}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: on ? "#14532d" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
              {sub && <span dir="ltr" style={{ display: "block", fontSize: 11, color: "#64748b", textAlign: "right", whiteSpace: "nowrap" }}>{sub}</span>}
              <span style={{ display: "block", fontSize: 10.5, color: "#94a3b8", marginTop: 1 }}>{meta}</span>
            </span>
            {unread > 0 && <span style={{ position: "absolute", top: 6, insetInlineEnd: 8, background: "#dc2626", color: "#fff", borderRadius: 999, padding: "0 7px", fontSize: 10.5, fontWeight: 800, lineHeight: "18px", minWidth: 18, textAlign: "center" }}>{unread}</span>}
            {extra}
          </button>
        );
        return (
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 16, padding: 10, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <b style={{ fontSize: 13, color: "#0f172a" }}>📱 {isSingle ? (scope === "daleelak" ? "رقم دليلك الرسمي" : "الرقم الرئيسي للشركة") : "أرقام واتساب للأعمال"}</b>
              {!isSingle && <span style={{ fontSize: 11.5, color: "#64748b" }}>{nums.length} رقم · {totMsgs} رسالة{totUnread ? ` · ${totUnread} غير مقروءة` : ""}</span>}
              <span style={{ marginInlineStart: "auto", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {!isSingle && nums.length > 6 && <input value={numQ} onChange={(e) => setNumQ(e.target.value)} placeholder="بحث في الأرقام…" style={{ ...inputSt, padding: "5px 10px", fontSize: 12, width: 160 }} />}
                {isAdmin && biz !== "all" && biz !== "غير محدد" && (
                  <button onClick={async () => {
                    const cur = nums.find((x) => x.display_number === biz);
                    const name = window.prompt(`اسم الرقم ${biz} (مثل اسم الموظف أو النشاط):`, cur?.name || "");
                    if (name === null) return;
                    try { await waBiz.renameNumber(biz, name.trim()); toast.success("حُفظ الاسم ✓"); loadNums(); }
                    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
                  }} style={{ ...btnSt("#7c3aed"), padding: "5px 10px", fontSize: 11.5 }}>✏️ تسمية الرقم</button>
                )}
                <button onClick={() => { loadNums(); loadConvs(); active && openThread(active); }} style={{ ...btnSt("#64748b"), padding: "5px 10px", fontSize: 11.5 }}><FiRefreshCw size={12} /> تحديث</button>
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))", gap: 8 }}>
              {!isSingle && card("all", biz === "all", () => pickN("all"), "∑", "كل الأرقام", "", `${totMsgs} رسالة`, totUnread)}
              {shownNums.map((n) => card(n.display_number, isSingle || biz === n.display_number, () => !isSingle && pickN(n.display_number),
                n.is_company_main ? "🏢" : n.is_daleelak ? "📍" : (String(n.name || "").trim()[0] || "#"),
                n.name || (n.display_number === "غير محدد" ? "غير محدد (رسائل قديمة)" : "بلا اسم"),
                n.display_number === "غير محدد" ? "" : fmtNum(n.display_number),
                `💬 ${n.total || 0} · آخر نشاط ${ago(n.last_at)}`, Number(n.unread) || 0,
                official(n) ? <span style={{ position: "absolute", bottom: 6, insetInlineEnd: 8, fontSize: 9.5, fontWeight: 800, color: "#16a34a", background: "#dcfce7", borderRadius: 999, padding: "0 6px" }}>رسمي</span> : null))}
              {!isSingle && numQ.trim() && shownNums.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8", padding: 10 }}>لا يوجد رقم مطابق</div>}
            </div>
          </div>
        );
      })()}
      {restricted && ( /* v3.4.841 */
        <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 10, fontSize: 12, fontWeight: 700, background: restricted.linked ? "#ede9fe" : "#fef2f2", color: restricted.linked ? "#5b21b6" : "#991b1b", border: `1px solid ${restricted.linked ? "#ddd6fe" : "#fecaca"}` }}>
          {restricted.linked ? "↪ تظهر لك المحادثات المحوّلة إليك فقط — غيرها لا يظهر ولا يمكن فتحه أو الرد عليه." : "⚠ حسابك غير مربوط بحساب في تطبيق المنظم التسويقي، فلا تظهر لك أي محادثة — اطلب من الأدمن ربط حساب التطبيق بمستخدمك أو منحك صلاحية «عرض كل المحادثات»."}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* قائمة المحادثات */}
        <div style={{ width: 340, maxWidth: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: 8, borderBottom: "1px solid #f1f5f9" }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باسم أو رقم…" style={{ ...inputSt, width: "100%", boxSizing: "border-box" }} />
          </div>
          <div style={{ maxHeight: 560, overflowY: "auto" }}>
            {(convs || []).map((c) => (
              <div key={c.phone} onClick={() => openThread(c.phone)}
                style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc", cursor: "pointer", background: active === c.phone ? "#f0fdf4" : "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 13 }}>{c.contact_name || c.phone}</span>
                  <span style={{ fontSize: 10.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtT(c.last_at)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 11.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
                    {c.last_dir === "outbound" ? "↩ " : ""}{c.last_body || "—"}
                  </span>
                  {c.unread > 0 && <span style={{ background: "#16a34a", color: "#fff", borderRadius: 10, padding: "0 7px", fontSize: 10.5, fontWeight: 800, alignSelf: "center" }}>{c.unread}</span>}
                </div>
                {c.contact_name && <div style={{ fontSize: 10.5, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>{c.phone}</div>}
                {c.assignee_name && <div style={{ marginTop: 3 }}><span style={{ fontSize: 10, fontWeight: 800, background: "#ede9fe", color: "#5b21b6", borderRadius: 10, padding: "1px 8px" }}>↪ محوّلة إلى {c.assignee_name}</span></div>} {/* v3.4.161 */}
              </div>
            ))}
            {convs && !convs.length && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>لا توجد محادثات</div>}
            {!convs && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>جارٍ التحميل…</div>}
          </div>
        </div>
        {/* خيط المحادثة */}
        <div style={{ flex: 1, minWidth: 320, background: "#f0f4f0", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, minHeight: 300 }}>
          {!active && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>اختر محادثة من القائمة لاستعراضها</div>}
          {/* v3.4.031 — ترويسة الخيط: اسم المرسل ورقمه (الرقم تحت كل فقاعة هو رقم الأعمال المستقبِل وليس المرسل) */}
          {active && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>👤</span>
              <span style={{ fontWeight: 800, fontSize: 13.5 }}>{(convs || []).find((c) => c.phone === active)?.contact_name || "بدون اسم"}</span>
              <span dir="ltr" style={{ fontSize: 12.5, color: "#16a34a", fontWeight: 800 }}>{active}</span>
              {/* v3.4.161 — التحويل إلى مسوّق (يتطلب اختيار رقم أعمال محدد) */}
              {biz !== "all" && biz !== "غير محدد" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginInlineStart: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: threadAssignee ? "#5b21b6" : "#64748b" }}>{threadAssignee ? `↪ محوّلة إلى: ${threadAssignee.name}` : "غير محوّلة"}</span>
                  <select value={threadAssignee?.id || ""} disabled={assigning} onChange={(e) => assignTo(e.target.value ? Number(e.target.value) : null)}
                    title="تحويل هذه المحادثة إلى حساب مسوّق — تظهر له في تبويب الرقم الرسمي داخل التطبيق"
                    style={{ padding: "4px 8px", border: "1px solid #c4b5fd", borderRadius: 6, fontSize: 11.5, fontFamily: "inherit", background: "#fff", color: "#1e293b", maxWidth: 220 }}>
                    <option value="">— تحويل إلى… / إلغاء التحويل —</option>
                    {assignees.map((u) => <option key={u.id} value={u.id}>{u.display_name} ({u.assigned_count})</option>)}
                  </select>
                </div>
              ) : (
                <span style={{ fontSize: 10.5, color: "#94a3b8", marginInlineStart: 8 }}>اختر رقم أعمال محددًا لتحويل المحادثة</span>
              )}
              <span style={{ fontSize: 10.5, color: "#94a3b8", marginInlineStart: "auto" }}>☎ تحت كل رسالة = رقم الأعمال المستقبِل</span>
            </div>
          )}
          {active && !thread && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div>}
          {active && thread && (
            <div style={{ maxHeight: 560, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {thread.map((m) => (
                <div key={m.id} style={{ alignSelf: m.direction === "outbound" ? "flex-start" : "flex-end", maxWidth: "78%",
                  background: m.direction === "outbound" ? "#dcfce7" : "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 10px" }}>
                  <WaMedia m={m} src={`/api/wa-business/media/${m.id}`} /> {/* v3.4.091 */}
                  <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap" }}>{m.media_id && isPlaceholderBody(m.message_body) ? null : (m.message_body || (m.template_name ? `📋 قالب: ${m.template_name}` : (m.media_id ? "" : `[${m.message_type}]`)))}</div>
                  <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 3, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {m.biz_display_number && <span dir="ltr">☎ {m.biz_display_number}</span>}
                    <span>{fmtT(m.created_at)}</span>
                    {m.direction === "outbound" && <span>{ST[m.status] || m.status}</span>}
                  </div>
                </div>
              ))}
              {!thread.length && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>لا توجد رسائل</div>}
            </div>
          )}
          {/* v3.4.038 — صندوق الرد: نص حر ضمن نافذة 24 ساعة من آخر رسالة واردة — v3.4.114: بعلم الرد */}
          {active && thread && !canReply && <div style={{ marginTop: 10, fontSize: 11.5, color: "#94a3b8", textAlign: "center" }}>الاطلاع فقط — الرد يحتاج صلاحية «الرد على المحادثات»</div>}
          {active && thread && canReply && (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="اكتب ردًا… (يُرسل من نفس رقم الأعمال المستقبِل)"
                style={{ ...inputSt, flex: 1, boxSizing: "border-box" }} disabled={sending} />
              <button onClick={sendReply} disabled={sending || !replyText.trim()}
                style={{ ...btnSt("#16a34a"), padding: "8px 20px", fontSize: 12.5, opacity: sending || !replyText.trim() ? 0.6 : 1 }}>
                {sending ? "…" : "إرسال ↵"}
              </button>
            </div>
          )}
        </div>
      </div>
      <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 8 }}>
        الرسائل القديمة (قبل هذا التحديث) تظهر تحت «غير محدد» لأن ميتا لم يكن يوسمها برقم الأعمال عندنا — الرسائل الجديدة تُوسم برقمها تلقائيًا.
      </div>
      </>}
    </div>
  );
}

// ── v3.4.110 — واتس المسوقين: أرقام المسوّقين الشخصية المربوطة عبر تطبيق المنظم التسويقي (QR/رمز) — استعراض فقط ──
function MarketersWaTab() {
  const [sessions, setSessions] = useState(null);
  const [sel, setSel] = useState(null);     // جلسة المسوّق المختارة
  const [q, setQ] = useState("");
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const loadSessions = useCallback(() => {
    waBiz.pwaSessions().then((r) => setSessions(r.data?.sessions || [])).catch((e) => { toast.error(e.response?.data?.error || "فشل تحميل الجلسات"); setSessions([]); });
  }, []);
  const loadConvs = useCallback(() => {
    if (!sel) { setConvs(null); return; }
    waBiz.pwaConversations(sel.portal_user_id, q.trim() || undefined)
      .then((r) => setConvs(r.data?.conversations || []))
      .catch((e) => { toast.error(e.response?.data?.error || "فشل التحميل"); setConvs([]); });
  }, [sel, q]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  const openThread = (phone) => {
    if (!sel) return;
    setActive(phone); setThread(null);
    waBiz.pwaThread(sel.portal_user_id, phone).then((r) => setThread(r.data?.messages || []))
      .catch((e) => toast.error(e.response?.data?.error || "فشل فتح المحادثة"));
  };
  const fmtT = (t) => String(t || "").slice(0, 16).replace("T", " ");
  const STATUS = { connected: ["متصل", "#16a34a", "#f0fdf4"], restoring: ["يُعاد تشغيله", "#b45309", "#fffbeb"], qr_pending: ["بانتظار الربط", "#b45309", "#fffbeb"], disconnected: ["منقطع", "#b91c1c", "#fef2f2"], none: ["غير مربوط", "#64748b", "#f8fafc"] };
  const chip = (s) => {
    const st = STATUS[s.status] || STATUS.none, on = sel?.portal_user_id === s.portal_user_id;
    return (
      <button key={s.portal_user_id} onClick={() => { setSel(s); setActive(null); setThread(null); }} title={s.phone || ""}
        style={{ padding: "6px 14px", borderRadius: 12, fontSize: 12, fontWeight: on ? 800 : 600, cursor: "pointer", fontFamily: "inherit", textAlign: "right",
          border: on ? "2px solid #2563eb" : "1px solid #e2e8f0", background: on ? "#eff6ff" : "#fff", color: on ? "#1d4ed8" : "#475569" }}>
        {s.display_name || s.user_phone}
        {s.phone && <span dir="ltr" style={{ fontSize: 10.5, opacity: 0.8, marginInlineStart: 6 }}>+{s.phone}</span>}
        <span style={{ marginInlineStart: 6, fontSize: 10, fontWeight: 800, color: st[1], background: st[2], borderRadius: 8, padding: "1px 7px" }}>{st[0]}</span>
        <span style={{ marginInlineStart: 6, fontSize: 10.5, opacity: 0.7 }}>({s.conversations} محادثة · {s.total} رسالة)</span>
      </button>
    );
  };
  return (
    <div>
      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#1e40af", lineHeight: 1.8 }}>
        الأرقام الشخصية التي ربطها المسوّقون من تطبيق المنظم التسويقي (تبويب «واتسابي»). كل الرسائل الواردة والصادرة تُحفظ في النظام تلقائيًا. الاستعراض هنا للاطلاع فقط — الرد يكون من جوال المسوّق أو من التطبيق.
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {sessions === null && <span style={{ fontSize: 12, color: "#94a3b8" }}>جارٍ التحميل…</span>}
        {sessions && !sessions.length && <span style={{ fontSize: 12.5, color: "#94a3b8" }}>لا يوجد مسوّق ربط واتسابه الشخصي بعد</span>}
        {(sessions || []).map(chip)}
        <button onClick={() => { loadSessions(); loadConvs(); active && openThread(active); }} style={{ ...btnSt("#64748b"), padding: "5px 10px", fontSize: 11.5 }}><FiRefreshCw size={12} /> تحديث</button>
      </div>
      {sel && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 340, maxWidth: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: 8, borderBottom: "1px solid #f1f5f9" }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باسم أو رقم…" style={{ ...inputSt, width: "100%", boxSizing: "border-box" }} />
            </div>
            <div style={{ maxHeight: 560, overflowY: "auto" }}>
              {(convs || []).map((c) => (
                <div key={c.phone} onClick={() => openThread(c.phone)}
                  style={{ padding: "10px 12px", borderBottom: "1px solid #f8fafc", cursor: "pointer", background: active === c.phone ? "#eff6ff" : "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 13 }}>{c.contact_name || c.phone}</span>
                    <span style={{ fontSize: 10.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtT(c.last_at)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 3 }}>
                    <span style={{ fontSize: 11.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
                      {c.last_dir === "outbound" ? "↩ " : ""}{c.last_body || "—"}
                    </span>
                    <span style={{ fontSize: 10.5, color: "#94a3b8", alignSelf: "center" }}>{c.total}</span>
                  </div>
                  {c.contact_name && <div style={{ fontSize: 10.5, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>{c.phone}</div>}
                </div>
              ))}
              {convs && !convs.length && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>لا توجد محادثات محفوظة لهذا المسوّق بعد</div>}
              {!convs && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>جارٍ التحميل…</div>}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 320, background: "#eef2ff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, minHeight: 300 }}>
            {!active && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>اختر محادثة من القائمة لاستعراضها</div>}
            {active && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>👤</span>
                <span style={{ fontWeight: 800, fontSize: 13.5 }}>{(convs || []).find((c) => c.phone === active)?.contact_name || "بدون اسم"}</span>
                <span dir="ltr" style={{ fontSize: 12.5, color: "#1d4ed8", fontWeight: 800 }}>+{active}</span>
                <span style={{ fontSize: 10.5, color: "#94a3b8", marginInlineStart: "auto" }}>من واتساب {sel.display_name || sel.user_phone}{sel.phone ? ` (+${sel.phone})` : ""}</span>
              </div>
            )}
            {active && !thread && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div>}
            {active && thread && (
              <div style={{ maxHeight: 560, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                {thread.map((m) => (
                  <div key={m.id} style={{ alignSelf: m.direction === "outbound" ? "flex-start" : "flex-end", maxWidth: "78%",
                    background: m.direction === "outbound" ? "#dbeafe" : "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 10px" }}>
                    {m.has_media && <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 800, marginBottom: 2 }}>📎 {m.message_type === "image" ? "صورة" : m.message_type === "video" ? "فيديو" : m.message_type === "ptt" || m.message_type === "audio" ? "رسالة صوتية" : "مرفق"} — على جوال المسوّق</div>}
                    <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap" }}>{m.message_body}</div>
                    <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 3, textAlign: "left" }}>{fmtT(m.created_at)}{m.direction === "outbound" ? " · ↩ المسوّق" : ""}</div>
                  </div>
                ))}
                {!thread.length && <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>لا توجد رسائل</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── v3.4.035 — الأرقام الجديدة أُنشئت كل واحد بحساب WABA مستقل — نشترك بويبهوكها دفعة واحدة بتوكن مؤقت ──
const DEFAULT_WABA_IDS = [
  "2340727836353942 — Meshari 0539555796",
  "2182336999243785 — Fatimah Qaed 0553663392",
  "586502847871139 — Rael 2 (Waad) 0553660506",
  "535138883016766 — Rael 1 (Fatimah Mousa) 0553665601",
  "525745570626187 — Rael Agar (Ahmed Saad) 0553662887",
  "493191883878540 — دليلك العقاري 0575752222",
].join("\n");
function WaSubscribeSection({ onDone, token, setToken, idsText, setIdsText }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const run = async () => {
    if (!token.trim()) return toast.error("الصق التوكن أولًا (من Graph API Explorer لنفس تطبيق الويبهوك)");
    setBusy(true); setResults(null);
    try {
      const r = await waBiz.subscribeWabas({ token: token.trim(), waba_ids: idsText.split("\n") });
      setResults(r.data?.results || []);
      const okN = (r.data?.results || []).filter((x) => x.ok).length;
      toast[okN ? "success" : "error"](`نجح الاشتراك في ${okN} من ${(r.data?.results || []).length} حساب`);
      onDone && onDone(); // v3.4.036 — نُبقي التوكن ليُستخدم في التشخيص مباشرة (يُمسح بمغادرة الصفحة)
    } catch (e) { toast.error(e.response?.data?.error || "فشل الاشتراك"); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 10, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: 12 }}>
      <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6 }}>🔗 اشتراك الويبهوك للحسابات (الحل بضغطة واحدة)</div>
      <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.8, marginBottom: 8 }}>
        من Graph API Explorer: اختر نفس تطبيق الويبهوك ← Generate Access Token مع صلاحية <b dir="ltr">whatsapp_business_management</b> ← انسخ التوكن والصقه هنا. التوكن يُستخدم فورًا ولا يُخزن.
      </div>
      <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="الصق التوكن هنا…" dir="ltr"
        style={{ ...inputSt, width: "100%", boxSizing: "border-box", marginBottom: 6 }} />
      <textarea value={idsText} onChange={(e) => setIdsText(e.target.value)} rows={6} dir="ltr"
        style={{ ...inputSt, width: "100%", boxSizing: "border-box", fontSize: 11, fontFamily: "monospace", marginBottom: 6 }} />
      <button onClick={run} disabled={busy} style={{ ...btnSt("#16a34a"), padding: "7px 18px", fontSize: 12.5 }}>{busy ? "جارٍ الاشتراك…" : "اشتراك الويبهوك لكل الحسابات أعلاه"}</button>
      {/* v3.4.037 — تسجيل رقم على تطبيقنا (بعد إزالة شراكة بيفاتيل): يحوّل أحداث رسائله إلينا */}
      <RegisterNumberRow token={token} />
      {results && (
        <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
          {results.map((r) => (
            <div key={r.waba_id} style={{ fontSize: 11.5 }}>
              <span dir="ltr">{r.waba_id}</span>: {r.ok ? "✅ تم الاشتراك" : <span style={{ color: "#b91c1c" }}>✗ {r.response?.error?.message || r.response?.error || JSON.stringify(r.response)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── v3.4.039 — إعدادات الاتصال بميتا: التوكن الدائم (System User) + معرف WABA — تُخزن في قاعدة البيانات وتسري فورًا ──
function WaMetaSettings() {
  const [st, setSt] = useState(null);
  const [token, setToken] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    waBiz.getSettings().then((r) => { setSt(r.data); setWabaId(r.data?.business_account_id || ""); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const [notesOpen, setNotesOpen] = useState(false); const [notes, setNotes] = useState(""); const [notesSaved, setNotesSaved] = useState(""); const [notesAt, setNotesAt] = useState(null); // v3.4.800
  useEffect(() => { waBiz.metaNotes().then((r) => { setNotes(r.data?.text || ""); setNotesSaved(r.data?.text || ""); setNotesAt(r.data?.updated_at || null); }).catch(() => {}); }, []);
  const saveNotes = async () => { try { const r = await waBiz.saveMetaNotes(notes); setNotesSaved(r.data?.text ?? notes); setNotesAt(new Date().toISOString()); toast.success("حُفظت البيانات الإضافية"); } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); } };
  const save = async () => {
    setBusy(true);
    try {
      const r = await waBiz.saveSettings({ ...(token.trim() ? { token: token.trim() } : {}), business_account_id: wabaId.trim() });
      if (r.data?.token_ok === false) toast.error(`حُفظ لكن اختبار التوكن فشل: ${r.data?.token_error || ""}`, { duration: 9000 });
      else toast.success("حُفظت الإعدادات ✓ وتسري فورًا");
      setToken(""); load();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 13.5 }}>🔐 إعدادات الاتصال بميتا (للإرسال والرد والتشخيص)</div>
        <button type="button" onClick={() => setNotesOpen((o) => !o)} title="ملاحظات حرة: حالة طلبات ميتا، التطبيقات والتوكنات، ما تم وما بقي…" style={{ marginInlineStart: "auto", padding: "5px 12px", borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", border: "1.5px solid #fcd34d", background: notesOpen ? "#fef3c7" : "#fffbeb", color: "#92400e" }}>📝 بيانات إضافية{notesSaved.trim() ? " •" : ""}</button>{/* v3.4.800 */}
      </div>
      {notesOpen && ( /* v3.4.800 — صندوق النص الحر */
        <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 12, padding: 12, marginBottom: 10, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#92400e", fontWeight: 800 }}>📝 بيانات إضافية — إعدادات ميتا {notesAt && <span style={{ fontWeight: 600, color: "#b45309" }}>· آخر حفظ {String(notesAt).slice(0, 16).replace("T", " ")}</span>}</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} placeholder="اكتب هنا: حالة طلب Tech Provider، معرّفات التطبيقات والـWABA، الأرقام وملاحظاتها، ما تم وما بقي…" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #fde68a", borderRadius: 8, padding: 10, fontSize: 13, fontFamily: "inherit", lineHeight: 1.7, background: "#fff", resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" disabled={notes === notesSaved} onClick={saveNotes} style={{ padding: "7px 16px", borderRadius: 10, fontSize: 12.5, fontWeight: 800, cursor: notes === notesSaved ? "default" : "pointer", fontFamily: "inherit", border: "none", background: notes === notesSaved ? "#e2e8f0" : "#b45309", color: notes === notesSaved ? "#64748b" : "#fff" }}>💾 حفظ</button>
            {notes !== notesSaved && <button type="button" onClick={() => setNotes(notesSaved)} style={{ padding: "7px 12px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: "1px solid #e2e8f0", background: "#fff", color: "#64748b" }}>تراجع</button>}
            <span style={{ fontSize: 11, color: "#94a3b8" }}>يحفظه الأدمن ويراه من يملك عرض إعدادات ميتا</span>
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.8, marginBottom: 8 }}>
        الصق توكن <b>System User الدائم</b> (من مستخدمي النظام في حافظة الأعمال، بصلاحيتي whatsapp_business_messaging + management) — يُحفظ في قاعدة البيانات ويعمل فورًا بلا إعادة نشر. الحالة الحالية:{" "}
        {st == null ? "…" : st.token_set ? <b style={{ color: "#166534" }}>✅ التوكن مضبوط ({st.token_hint}{st.token_from_db ? " — من النظام" : " — من إعدادات الخادم"})</b> : <b style={{ color: "#b91c1c" }}>❌ التوكن غير مضبوط — الرد والإرسال معطلان</b>}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <input value={token} onChange={(e) => setToken(e.target.value)} placeholder={st?.token_set ? "الصق توكنًا جديدًا للاستبدال (اختياري)…" : "الصق التوكن الدائم هنا…"} dir="ltr" type="password"
          style={{ ...inputSt, flex: 1, minWidth: 260, boxSizing: "border-box" }} />
        <input value={wabaId} onChange={(e) => setWabaId(e.target.value)} placeholder="معرف WABA الرئيسي (411295012076087)" dir="ltr"
          style={{ ...inputSt, width: 230 }} />
        <button onClick={save} disabled={busy || (!token.trim() && !wabaId.trim())} style={{ ...btnSt("#0f766e"), padding: "8px 18px", fontSize: 12.5, opacity: busy ? 0.6 : 1 }}>{busy ? "جارٍ الحفظ…" : "💾 حفظ واختبار"}</button>
      </div>
    </div>
  );
}

// ── v3.4.037 — تسجيل رقم على Cloud API تحت تطبيقنا: الخطوة الأخيرة لفصل رقم عن بيفاتيل ──
function RegisterNumberRow({ token }) {
  const [pnid, setPnid] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const reg = async () => {
    if (!token?.trim()) return toast.error("الصق التوكن أولًا في الحقل أعلاه");
    if (!window.confirm(`تسجيل الرقم (المعرف ${pnid}) على تطبيقنا؟ يُنفذ بعد إزالة شراكة بيفاتيل من حساب الرقم.`)) return;
    setBusy(true);
    try { await waBiz.registerNumber({ token: token.trim(), phone_number_id: pnid, pin }); toast.success("سُجل الرقم على تطبيقنا ✓ — أرسل رسالة تجريبية له الآن", { duration: 8000 }); }
    catch (e) { toast.error(e.response?.data?.error || "فشل التسجيل", { duration: 8000 }); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 10, borderTop: "1px dashed #86efac", paddingTop: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4 }}>📲 تسجيل رقم على تطبيقنا (الخطوة الأخيرة بعد إزالة شراكة بيفاتيل من حساب الرقم)</div>
      <div style={{ fontSize: 10.5, color: "#64748b", marginBottom: 6 }}>معرف الرقم (Phone Number ID) يظهر في جدول «الأرقام المسجلة» بعد التشخيص أو في WhatsApp Manager — وPIN هو رمز التحقق بخطوتين للرقم (6 أرقام؛ يُعاد تعيينه من شاشة الرقم في WhatsApp Manager إن كان مجهولًا)</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <input value={pnid} onChange={(e) => setPnid(e.target.value)} placeholder="Phone Number ID" dir="ltr" style={{ ...inputSt, width: 190 }} />
        <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN (6 أرقام)" dir="ltr" maxLength={6} style={{ ...inputSt, width: 120 }} />
        <button onClick={reg} disabled={busy || !pnid || pin.length !== 6} style={{ ...btnSt("#b45309"), padding: "7px 16px", fontSize: 12, opacity: busy || !pnid || pin.length !== 6 ? 0.6 : 1 }}>{busy ? "جارٍ التسجيل…" : "تسجيل الرقم"}</button>
      </div>
    </div>
  );
}

// ── v3.4.032 — تشخيص «الرسائل لا تصل»: يقارن أرقامنا بأرقام الـ WABA عند ميتا ويعرض أحداث الويبهوك ──
function WaBizDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(false);
  // v3.4.036 — التوكن ومعرفات WABA مشتركان بين الاشتراك والتشخيص (التشخيص يفحص ميتا بنفس التوكن المؤقت)
  const [token, setToken] = useState("");
  const [idsText, setIdsText] = useState(DEFAULT_WABA_IDS);
  const run = (tk = token, ids = idsText) => {
    setBusy(true);
    waBiz.webhookDiagnostics({ token: tk || undefined, waba_ids: (ids || "").split("\n") })
      .then((r) => setDiag(r.data)).catch((e) => toast.error(e.response?.data?.error || "فشل التشخيص")).finally(() => setBusy(false));
  };
  const digits = (s) => String(s || "").replace(/\D/g, "");
  const fmtT = (t) => String(t || "").slice(0, 16).replace("T", " ");
  const thD = { padding: "6px 10px", fontSize: 11, color: "#64748b", textAlign: "right", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
  const tdD = { padding: "6px 10px", fontSize: 11.5, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
  const metaByDigits = new Map((diag?.meta_numbers || []).map((m) => [digits(m.display_phone_number), m]));
  const evByDigits = new Map((diag?.per_number_events || []).map((e) => [digits(e.biz_display), e]));
  return (
    <div style={{ marginTop: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800, fontSize: 13.5 }}>🔍 تشخيص وصول الرسائل (لماذا رقم ما لا تظهر رسائله؟)</span>
        <button onClick={() => run()} disabled={busy} style={{ ...btnSt("#16a34a"), padding: "6px 16px", fontSize: 12 }}>{busy ? "جارٍ الفحص…" : "تشغيل التشخيص"}</button>
      </div>
      {/* v3.4.035 — الاشتراك بضغطة واحدة */}
      <WaSubscribeSection token={token} setToken={setToken} idsText={idsText} setIdsText={setIdsText} onDone={() => run()} />
      {diag && (
        <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
          {/* حكم لكل رقم مسجل عندنا */}
          <div>
            <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6 }}>الأرقام المسجلة في النظام:</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["الاسم", "الرقم", "معرف الرقم (للتسجيل)", "مسجل في WABA عند ميتا؟", "المنصة", "أحداث ويبهوك وصلت", "آخر حدث", "الحكم"].map((h) => <th key={h} style={thD}>{h}</th>)}</tr></thead>
                <tbody>
                  {(diag.numbers || []).map((n) => {
                    const m = metaByDigits.get(digits(n.display_number));
                    const ev = evByDigits.get(digits(n.display_number));
                    const onCloud = m && (!m.platform_type || m.platform_type === "CLOUD_API");
                    // v3.4.036 — حساب هذا الرقم: هل تطبيقنا مشترك بويبهوكه؟ (null = تعذر الفحص)
                    const wr = m && (diag.waba_report || []).find((w) => w.waba_id === m.waba_id);
                    const subd = wr?.subscribed_apps ? wr.subscribed_apps.length > 0 : null;
                    const verdict = ev?.events
                      ? "✅ الويبهوك يعمل لهذا الرقم"
                      : !diag.checked ? "❔ لم يصل أي حدث بعد — تعذر التحقق من ميتا (لا يوجد توكن). الصق التوكن أعلاه وأعد التشغيل"
                      : !m ? "⛔ الرقم ليس ضمن الحسابات المفحوصة — أضف معرف حسابه في المربع أعلاه أو انقله لحساب مشترك"
                      : !onCloud ? "⚠️ الرقم على تطبيق الجوال وليس Cloud API — رسائله تذهب للجوال ولا تصل للنظام"
                      : subd === false ? "⛔ حساب هذا الرقم غير مشترك بويبهوك تطبيقنا — استخدم زر الاشتراك أعلاه"
                      : subd ? "🟡 الاشتراك سليم — أرسل رسالة تجريبية للرقم ثم أعد التشخيص"
                      : "⚠️ مسجل على Cloud API لكن تعذر التحقق من الاشتراك";
                    return (
                      <tr key={n.display_number}>
                        <td style={{ ...tdD, fontWeight: 700 }}>{n.name || "—"}</td>
                        <td style={{ ...tdD, direction: "ltr", textAlign: "right" }}>{n.display_number}</td>
                        <td style={{ ...tdD, direction: "ltr", textAlign: "right", fontSize: 10.5, color: "#64748b" }}>{m?.id || "—"}</td>{/* v3.4.037 — للتسجيل على تطبيقنا */}
                        <td style={tdD}>{m ? `نعم (${m.verified_name || ""} ${m.status || ""})` : diag.checked ? "لا" : "؟"}</td>
                        <td style={tdD}>{m ? (m.platform_type || "غير معروف") : "—"}</td>
                        <td style={{ ...tdD, fontWeight: 800 }}>{ev?.events || 0}</td>
                        <td style={tdD}>{ev?.last_event ? fmtT(ev.last_event) : "—"}</td>
                        <td style={{ ...tdD, whiteSpace: "normal", minWidth: 220 }}>{verdict}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* v3.4.036 — حالة كل حساب WABA: أرقامه + هل تطبيقنا مشترك بويبهوكه */}
          {diag.meta_error && <div style={{ fontSize: 11.5, color: "#b45309" }}>ℹ️ {diag.meta_error}</div>}
          {(diag.waba_report || []).length > 0 && (
            <div>
              <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6 }}>حسابات WABA المفحوصة:</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <thead><tr>{["حساب WABA", "أرقامه", "مشترك بويبهوك تطبيقنا؟"].map((h) => <th key={h} style={thD}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(diag.waba_report || []).map((w) => (
                      <tr key={w.waba_id}>
                        <td style={{ ...tdD, direction: "ltr", textAlign: "right" }}>{w.waba_id}</td>
                        <td style={{ ...tdD, whiteSpace: "normal" }}>{w.error ? <span style={{ color: "#b91c1c" }}>{w.error}</span> : (w.numbers || []).map((n) => n.display_phone_number).join("، ") || "—"}</td>
                        <td style={tdD}>{w.subscribed_apps == null ? "تعذر الفحص" : w.subscribed_apps.length ? `✅ نعم (${w.subscribed_apps.map((a) => a?.whatsapp_business_api_data?.name || a?.name || a?.id || "?").join("، ")})` : <span style={{ color: "#b91c1c" }}>⛔ لا</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {diag.checked && (diag.meta_numbers || []).filter((m) => !(diag.numbers || []).some((n) => digits(n.display_number) === digits(m.display_phone_number))).length > 0 && (
            <div style={{ fontSize: 11.5, color: "#64748b" }}>
              أرقام في WABA لم تُسجل عندنا بعد: {(diag.meta_numbers || []).filter((m) => !(diag.numbers || []).some((n) => digits(n.display_number) === digits(m.display_phone_number))).map((m) => `${m.verified_name || ""} ${m.display_phone_number}`).join("، ")}
            </div>
          )}
          {/* آخر أحداث الويبهوك */}
          <div>
            <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6 }}>آخر أحداث الويبهوك ({(diag.recent_events || []).length}):</div>
            {(diag.recent_events || []).length === 0 && <div style={{ fontSize: 11.5, color: "#b91c1c" }}>لا توجد أحداث ويبهوك إطلاقًا — تحقق من رابط الويبهوك واشتراكه في تطبيق المطورين.</div>}
            <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <tbody>
                  {(diag.recent_events || []).map((e) => (
                    <tr key={e.id}>
                      <td style={tdD}>{fmtT(e.created_at)}</td>
                      <td style={tdD}>{e.kind}</td>
                      <td style={{ ...tdD, direction: "ltr", textAlign: "right" }}>{e.biz_display || "—"}</td>
                      <td style={{ ...tdD, color: "#b91c1c" }}>{e.error || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── الإعدادات (أدمن) ──
// v3.4.806 — دوال ثابتة (خارج المكوّنات) حتى لا يعيد NotesBox التحميل مع كل رسم
const pbxNotesLoad = () => yeastar.notes("pbx"), pbxNotesSave = (t) => yeastar.saveNotes("pbx", t);
const tgNotesLoad = () => yeastar.notes("tg"), tgNotesSave = (t) => yeastar.saveNotes("tg", t);
// ── v3.4.808 — إدخال المتصلين تلقائيًا إلى CRM (إعدادات + سجل + تشغيل يدوي) ──
function CallIntakeTab() {
  const [data, setData] = useState(null); const [busy, setBusy] = useState(""); const [days, setDays] = useState(7);
  const load = useCallback(() => yeastar.callIntake().then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.error || "فشل التحميل")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!data?.running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [data?.running, load]);
  const save = async (patch) => { setBusy("save"); try { await yeastar.saveCallIntake({ ...data.settings, ...patch }); toast.success("حُفظ ✓"); load(); } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); } setBusy(""); };
  const runNow = async () => {
    setBusy("run");
    try { const r = await yeastar.runCallIntake(days); const s = r.data?.summary || {}; toast.success(`تمت المعالجة: ${s.created || 0} عميل جديد، ${s.noted || 0} ملاحظة، ${s.skipped_known || 0} معروف`, { duration: 8000 }); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل التشغيل"); }
    setBusy("");
  };
  if (!data) return <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>جارٍ التحميل…</div>;
  const st = data.state || {}, lr = st.last_run, s = data.settings;
  const Sum = ({ r }) => r ? <span style={{ fontSize: 11.5, color: "#475569" }}>{String(r.started_at || "").slice(0, 16).replace("T", " ")} · {r.reason} · فُحص {r.scanned} · جديد <b style={{ color: "#166534" }}>{r.created}</b> · ملاحظات {r.noted} · معروف {r.skipped_known} · فائتة {r.skipped_missed} · بلا حساب {r.skipped_unmapped}{r.error ? <b style={{ color: "#b91c1c" }}> · خطأ: {r.error}</b> : null}</span> : <span style={{ fontSize: 11.5, color: "#94a3b8" }}>لم يُشغَّل بعد</span>;
  const ACTION = { created: ["✅ أُنشئ عميل محتمل", "#166534"], noted: ["📝 ملاحظة على عميل محتمل", "#0369a1"], skipped_known: ["معروف مسبقًا", "#64748b"], skipped_known_prospect: ["عميل محتمل موجود", "#64748b"] };
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 6 }}>📥 إدخال المتصلين تلقائيًا إلى CRM</div>
        <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.8, marginBottom: 10 }}>
          كل مكالمة خارجية <b>مُجابة</b> (واردة أجاب عليها مسوّق أو صادرة أجراها) من رقم غير مسجّل في النظام (ليس عميلًا ولا مسوّقًا ولا عميلًا محتملًا — المطابقة بآخر 9 أرقام) تُنشئ عميلًا محتملًا باسم مؤقت «متصل 05…» مُسنَدًا لصاحب التحويلة كأنه أضافه بنفسه، بتاريخ المكالمة. المكالمات الفائتة تُتجاهل، ومكالمات التحويلات غير المرتبطة بحساب بوابة تُتجاهل. تعمل كل 5 دقائق.
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}><input type="checkbox" checked={!!s.enabled} disabled={busy === "save"} onChange={(e) => save({ enabled: e.target.checked })} /> مفعّل</label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer" }}><input type="checkbox" checked={!!s.note_existing} disabled={busy === "save"} onChange={(e) => save({ note_existing: e.target.checked })} /> تسجيل المكالمات كملاحظة على العملاء المحتملين الموجودين</label>
          {data.running && <span style={{ fontSize: 12, color: "#0369a1", fontWeight: 700 }}>⏳ معالجة جارية الآن…</span>}
        </div>
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>التحويلات المرتبطة بحسابات البوابة (المصدر الوحيد للإسناد):</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(data.accounts || []).map((a) => { const off = (s.exclude_exts || []).includes(a.ext); return (
              <label key={a.id} title={off ? "مستثنى — اضغط للتفعيل" : "اضغط للاستثناء"} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, borderRadius: 8, padding: "3px 8px", cursor: "pointer", border: `1px solid ${off ? "#fecaca" : "#99f6e4"}`, background: off ? "#fef2f2" : "#f0fdfa", color: off ? "#991b1b" : "#0f766e" }}>
                <input type="checkbox" checked={!off} disabled={busy === "save"} onChange={(e) => save({ exclude_exts: e.target.checked ? (s.exclude_exts || []).filter((x) => x !== a.ext) : [...(s.exclude_exts || []), a.ext] })} />{a.ext} {a.display_name}
              </label>); })}
            {!(data.accounts || []).length && <span style={{ color: "#94a3b8" }}>لا توجد حسابات بوابة مرتبطة بتحويلة — اربطها من «إدارة حسابات البوابة» (حقل التحويلة)</span>}
          </div>
        </div>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>⏱ آخر تشغيل</div>
        <div><Sum r={lr} /></div>
        {st.last_backfill && <div style={{ marginTop: 4 }}><span style={{ fontSize: 11.5, fontWeight: 700, color: "#475569" }}>المعالجة الرجعية: </span><Sum r={st.last_backfill} /></div>}
        {st.totals && <div style={{ marginTop: 4, fontSize: 11.5, color: "#475569" }}>الإجمالي منذ التفعيل: <b>{st.totals.created}</b> عميل محتمل جديد، {st.totals.noted} ملاحظة</div>}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          <span style={{ fontSize: 12 }}>معالجة يدوية لآخر</span>
          <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value) || 1)} style={{ ...inputSt, width: 70, padding: "5px 8px" }} />
          <span style={{ fontSize: 12 }}>يومًا</span>
          <button type="button" onClick={runNow} disabled={!!busy || data.running} style={btnSt("#0f766e")}>{busy === "run" ? "⏳ جارٍ…" : "▶ تشغيل الآن"}</button>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>المكالمات المعالجة سابقًا لا تتكرر.</span>
        </div>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>📋 آخر النتائج ({(data.recent || []).length})</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "#f1f5f9" }}>{["وقت المكالمة", "الاتجاه", "الرقم", "التحويلة / المسوّق", "النتيجة", "العميل المحتمل"].map((h) => <th key={h} style={{ padding: "8px", textAlign: "right", fontSize: 11, color: "#475569", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.recent || []).map((r) => { const a = ACTION[r.action] || [r.action, "#64748b"]; return (
                <tr key={r.cdr_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{String(r.call_time || "").slice(0, 16).replace("T", " ")}</td>
                  <td style={{ padding: "6px 8px" }}>{r.direction === "inbound" ? "📥 واردة" : "📤 صادرة"}</td>
                  <td style={{ padding: "6px 8px", direction: "ltr", textAlign: "right", fontFamily: "monospace" }}>{r.phone}</td>
                  <td style={{ padding: "6px 8px" }}>{r.ext} {r.account_name || ""}</td>
                  <td style={{ padding: "6px 8px", color: a[1], fontWeight: 700 }}>{a[0]}{r.action.startsWith("skipped") && r.note ? <span style={{ color: "#94a3b8", fontWeight: 500 }}> — {r.note}</span> : null}</td>
                  <td style={{ padding: "6px 8px" }}>{r.prospect_id ? <a href={`/client-prospects?open=${r.prospect_id}`} target="_blank" rel="noreferrer" style={{ color: "#1d4ed8", fontWeight: 700, textDecoration: "none" }}>{r.prospect_name || "#" + r.prospect_id} ↗</a> : "—"}</td>
                </tr>); })}
              {!(data.recent || []).length && <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>لا نتائج بعد</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function SettingsTab({ onSaved }) {
  const [f, setF] = useState({ base_url: "", client_id: "", client_secret: "", default_caller: "", admin_url: "", tg_admin_url: "" });
  const [hasSecret, setHasSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    yeastar.settings().then((r) => {
      const d = r.data || {};
      setF({ base_url: d.base_url || "", client_id: d.client_id || "", client_secret: "", default_caller: d.default_caller || "",
        admin_url: d.admin_url || "", tg_admin_url: d.tg_admin_url || "" }); // v3.3.936
      setHasSecret(!!d.has_secret);
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try { await yeastar.saveSettings(f); toast.success("حُفظت الإعدادات ✓"); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  const row = (label, key, props = {}) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 4 }}>{label}</div>
      <input value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} dir="ltr" style={{ ...inputSt, width: "100%" }} {...props} />
    </div>
  );
  return (
    <div style={{ maxWidth: 620 }}>
      {/* v3.3.936 — روابط فتح واجهات الأجهزة (تعمل من داخل شبكة المكتب) */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>🖥 واجهات الأجهزة</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {f.admin_url && <a href={f.admin_url} target="_blank" rel="noreferrer" style={{ ...btnSt("#0369a1"), textDecoration: "none" }}>☎️ واجهة السنترال (PBX)</a>}
          {f.tg_admin_url && <a href={f.tg_admin_url} target="_blank" rel="noreferrer" style={{ ...btnSt("#7c3aed"), textDecoration: "none" }}>📡 واجهة الجايتواي (TG)</a>}
          <span style={{ marginInlineStart: "auto" }} /><NotesBox load={pbxNotesLoad} save={pbxNotesSave} title="ربط السنترال (Yeastar)" placeholder="اكتب هنا: بيانات دخول واجهة السنترال، RAS، أرقام الشرائح وDID، ما تم وما بقي…" hint="يراه ويحرره من يملك صلاحية إعدادات الكول سنتر" />{/* v3.4.806 */}
        </div>
        {row("رابط واجهة السنترال", "admin_url", { placeholder: "https://192.168.100.250:8088/login" })}
        {row("رابط واجهة الجايتواي", "tg_admin_url", { placeholder: "http://192.168.100.254/cgi/WebCGI?1000" })}
        <div style={{ fontSize: 10.5, color: "#94a3b8" }}>هذه روابط داخلية — تفتح من أجهزة شبكة المكتب فقط.</div>
      </div>
      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 12, marginBottom: 14, fontSize: 12, color: "#1e40af", lineHeight: 1.8 }}>
        <b>خطوات التفعيل في السنترال:</b> من واجهة Yeastar → Integrations → API → فعّل الـAPI وانسخ <b>Client ID</b> و<b>Client Secret</b>، وأضف IP خادم النظام إلى القائمة المسموحة إن وُجدت.<br />
        <b>مهم:</b> خادم النظام يعمل سحابيًا (DigitalOcean) — عنوان <code>192.168.100.250</code> داخلي ولن يصل إليه. استخدم رابطًا عامًا: إما Port Forwarding للمنفذ 8088 على IP الإنترنت الثابت لديكم، أو فعّل Remote Access (FQDN) من Yeastar وضع رابطه هنا.
      </div>
      {row("رابط السنترال (مع المنفذ)", "base_url", { placeholder: "https://pbx.example.com:8088 أو https://xxxx.ras.yeastar.com" })}
      {row("Client ID", "client_id", { placeholder: "من Integrations → API" })}
      {row(`Client Secret ${hasSecret ? "(محفوظ — اتركه فارغًا للإبقاء عليه)" : ""}`, "client_secret", { placeholder: hasSecret ? "••••••••" : "من Integrations → API", type: "password" })}
      {row("التحويلة الافتراضية للاتصال (اختياري)", "default_caller", { placeholder: "تُستخدم إذا لم يحدد المستخدم تحويلته" })}
      <button onClick={save} disabled={busy} style={btnSt("#0f766e")}>{busy ? "جارٍ الحفظ…" : "💾 حفظ واختبار الاتصال"}</button>
    </div>
  );
}

export default CallCenterPage;
